import { logger } from "@/lib/logger";
import { getDomainConfig } from '@/config/domainConfig';
import { InsightType } from '@strivelabs/ui-components-prompt-registry';
import type {
  KnowledgeHubCreateParams,
  KnowledgeHubListResponse,
  KnowledgeHubResponse,
  KnowledgeHubUpdateParams,
} from '@/types/knowledgeHub';
import type {
  AssignmentCategoryValue,
  AssignmentListResponse,
  AssignmentResponse,
} from '@/types/assignment';
import type { ConnectionsCardData, PublicAppTool } from '@/types/connections';
import type { GlossaryEntry } from '@/types/glossary';
import type {
  TenantSkillCreateParams,
  TenantSkillResponse,
  TenantSkillsResponse,
  TenantSkillUpdateParams,
} from '@/constants/skills';

// Custom error class that includes HTTP status code for proper retry handling
export class ApiError extends Error {
  status: number;
  code?: string;

  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

// Helper to throw API errors with status codes
const throwApiError = (message: string, status: number): never => {
  throw new ApiError(message, status);
};

// Throw an ApiError, surfacing the server-provided `{ message }` from a non-OK
// response body when present (duplicate handle, reserved prefix, etc.) and
// falling back to a generic message when the body isn't JSON.
const throwApiErrorWithBody = async (
  response: Response,
  fallback: string,
): Promise<never> => {
  const text = await response.text();
  let message = fallback;
  try {
    const parsed = JSON.parse(text);
    if (parsed?.message) message = parsed.message;
  } catch {
    /* non-JSON body — keep the fallback */
  }
  return throwApiError(message, response.status);
};

type HttpMethodType = 'GET' | 'POST' | 'DELETE' | 'PATCH' | 'PUT';

interface OauthPayload {
  app_name: string;
  display_name: string;
  auth_name: string;
  token_type: string;
  access_token: string;
  refresh_token: string;
  principal_id: string;
  principal_name: string;
  principal_email: string;
}

interface OauthCredentials {
  credential: OauthPayload;
}

interface BasicAuthPayload {
  app_name: string;
  display_name: string;
  auth_name: string;
  token_type: 'basic';
  username: string;
  password: string;
  principal_id?: string;
  principal_name?: string;
  principal_email?: string;
  custom_data?: Record<string, unknown>;
}

interface BasicAuthCredentials {
  credential: BasicAuthPayload;
}

interface ApiKeyPayload {
  app_name: string;
  display_name: string;
  auth_name: string;
  token_type: 'api_key';
  api_key: string;
  principal_id?: string;
  principal_name?: string;
  principal_email?: string;
  custom_data?: Record<string, unknown>;
}

interface ApiKeyCredentials {
  credential: ApiKeyPayload;
}

type CreateCredentialPayload = OauthCredentials | BasicAuthCredentials | ApiKeyCredentials;

interface PatchCredentialCustomData {
  credential: { custom_data: Record<string, unknown> };
}

interface TenantSettings {
  [key: string]: {
    credential_id: string;
  };
}

interface CreateAgentInstancePayload {
  agent_instance: {
    agent_id: string;
    config?: Record<string, unknown>;
  };
}

interface UpdateAgentInstancePayload {
  agent_instance: {
    config: Record<string, unknown>;
  };
}

interface TriggerWorkflowPayload {
  trigger_workflow: {
    workflow_instance_id: string;
    data?: Array<{
      data: unknown;
      scenario: string;
    }>;
  };
}

interface TenantVariablePayload {
  tenant_variable: {
    variable_id: string,
    value: string
  }
}

interface QueryParams {
  page_size?: number;
  sort_by?: string;
  sort_order?: string;
  page?: number;
  title?: string;
}

interface ApprovalQueryParams extends QueryParams {
  workflow_instance_id?: string;
  workflow_execution_id?: string;
}

interface CardQueryParams {
  status?: 'ACTIVE' | 'ARCHIVED';
  sort_by?: 'created_at' | 'priority';
  sort_order?: 'asc' | 'desc';
  page_size?: number;
  page?: number;
}

interface CardUpdatePayload {
  card: {
    status: 'ACTIVE' | 'ARCHIVED';
  };
}

/**
 * A single chat attachment returned by the backend (a tenant-scoped file plus
 * a freshly presigned GET URL). Matches the server `FileResponseItem` shape and
 * the body of `POST /file`, so the same type covers history attachments and the
 * client-side optimistic preview after `uploadFile`.
 */
export interface ChatAttachment {
  id: string;
  mime_type: string;
  filename: string;
  size_bytes: number;
  url: string;
}

interface ChatHistoryItem {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  origin: string;
  created_at: string;
  updated_at: string;
  behaviour_action_state: Record<string, unknown>;
  files?: ChatAttachment[];
  citations?: ChatCitation[];
}

interface ChatHistoryQueryParams {
  sort_by?: 'created_at';
  sort_order?: 'asc' | 'desc';
  page_size?: number;
  page?: number;
}

interface ChatHistoryResponse {
  chat_history: ChatHistoryItem[];
}

export type WorkflowExecutionResourceType = 'INSIGHT' | 'CARD';

export interface WorkflowExecutionResourceInput {
  resource_type: WorkflowExecutionResourceType;
  resource_id: string;
}

export interface TriggerChatWorkflowPayload {
  trigger_workflow: {
    workflow_instance_id: string;
    resources?: WorkflowExecutionResourceInput[];
    message?: string;
  };
};

/** A resource row attached to a workflow execution (e.g. the insight a
 *  report-builder conversation is editing). */
export interface WorkflowExecutionResourceDTO {
  id: string;
  workflow_execution_id: string;
  resource_type: WorkflowExecutionResourceType;
  resource_id: string;
  created_at?: string;
  updated_at?: string;
}

interface UpdateBehaviourActionStatePayload {
  behaviour_action_state: Record<string, unknown>;
}

interface TenantPayload {
  tenant: {
    display_name: string,
    type: string,
    settings: object,
  },
  child: boolean,
}

interface TenantVariableRecord {
  variable_id: number | string;
  value: string;
  id: number;
  created_at: string;
  updated_at: string;
}

interface KeyValPayload {
  action: 'store' | 'get' | 'delete' | 'list';
  key_val: {
    app_name: string;
    entity: string;
    workflow_instance_id: string;
    key?: string;
    data?: unknown; // Can be any JSON-serializable data (object, array, primitive, etc.)
  };
}

interface AppAuth {
  short_name: string;
  type: string;
}

interface App {
  short_name: string;
  display_name: string;
  description: string;
  dataset_supported: boolean;
  auth: AppAuth[];
}

interface AppsResponse {
  apps: App[];
}

/**
 * Block element - individual UI components within an insight card
 */
interface BlockElement {
  id: string;
  type: string;
  content?: unknown; // Can be string, object, or any JSON-serializable data
  version?: string;
}

/**
 * Block configuration - contains visual and layout properties
 */
interface BlockConfig {
  heading?: string; // supports markdown (e.g., "Leads are **declining**")
  border_color?: 'red' | 'green' | 'yellow' | 'gray' | 'blue' | 'purple';
  layout?: {
    left: string[];  // element IDs for left column
    right: string[]; // element IDs for right column (stacked vertically)
  };
  [key: string]: unknown; // Allow additional config properties
}

/**
 * Block - represents a UI block (e.g., insight_card)
 */
interface Block {
  id: string;
  type: string; // e.g., "insight_card"
  version: string;
  config?: BlockConfig;
  block_elements?: BlockElement[];
}

interface Processor {
  id: string;
  block_id: string;
  data_sources?: unknown[];
  prompt?: string;
}

interface InsightDefinition {
  ui?: {
    blocks?: Block[];
  };
  processors?: Processor[];
}

interface Insight {
  id: string;
  tenant_id: string;
  display_name: string;
  definition?: InsightDefinition;
  status?: 'PENDING' | 'READY';
  type?: InsightType;
  html_url?: string;
  created_at: string;
  updated_at: string;
  last_report_at: string | null;
}

interface InsightsResponse {
  insights: Insight[];
}

/**
 * Insight report data structure
 */
interface InsightReportData {
  ui: {
    blocks: Block[]; // Array of insight_card blocks
  };
}

/**
 * Insight report response object
 */
interface InsightReport {
  id: number; // API returns integer
  insight_id: string;
  tenant_id: string;
  type?: string; // 'FLAT' | 'MULTI_PAGE'
  data: InsightReportData | Record<string, unknown>;
  created_at: string; // ISO 8601 date-time format
  updated_at: string; // ISO 8601 date-time format
}

interface InsightReportsResponse {
  insight_reports: InsightReport[];
}

type ChatAction =
  | { action: 'PROMPT'; role: 'user' | 'assistant'; content: string; file_ids?: string[] }
  | { action: 'CONTINUE' }
  | { action: 'UPDATE_CONTEXT'; context: string };

export interface ExecutePendingToolsParams {
  baseUrl?: string;
  workflow_instance_id: string;
  workflow_execution_id: string;
  tool_call_outputs?: Array<{ id: string; output: unknown }>;
  signal?: AbortSignal;
}

/**
 * Web-search / URL-context source attributed to an assistant response.
 * Distinct from the AEO SOR `Citation` type — this is the grounding source
 * a chat turn surfaces (ChatGPT/Claude-style "sources").
 */
export interface ChatCitation {
  url: string;
  title?: string;
}

export type ChatEvent =
  | { type: 'CONTENT_STREAM'; content: string } // Streaming JSON content character by character
  | { type: 'REASONING_STREAM'; content: string } // Streaming reasoning content from LLM
  | { type: 'TOOL_CALLS'; tool_calls: Array<{ id: string; name: string; description?: string; requires_approval?: boolean; hide_from_tool_call_chat_ui?: boolean }> }
  | { type: 'CHAT_METADATA'; chat: { llm_chat_id: string } } // Chat history metadata with persisted record ID
  | { type: 'CITATIONS'; chat_id: string; citations: ChatCitation[] } // Grounding sources tied to a persisted response
  | { type: 'TOOL_STREAM_START'; tool_call_id: string; tool_name: string; stream_to_ui?: boolean } // UI tool call starting to stream
  | { type: 'TOOL_STREAM_CONTENT'; tool_call_id: string; arguments_delta: string }; // UI tool call argument chunk

// Interface for chatWithWorkflowStream params
export interface ChatWithWorkflowStreamParams {
  baseUrl?: string;
  workflow_instance_id: string;
  workflow_execution_id: string;
  body: { chat: ChatAction };
  signal?: AbortSignal;
  onEvent: (event: ChatEvent) => void;
  onError?: (err: Error) => void;
  onDone?: () => void;
}

export interface Pagination {
  page: number;
  page_size: number;
  total: number;
  total_pages: number;
}

// Interfaces for SOR Metrics
export type SORMetricDimension = {
  name: string;
  /** When set, the dim becomes a WHERE predicate instead of a group-by column. */
  value?: string;
} | {
  name: 'date';
  from: string;
  to: string;
  /**
   * Bucketing granularity. 'day' | 'week' | 'month' bucket the range for trend
   * charts; 'all' returns a single range-wide aggregate row (correct
   * sum-of-numerators / sum-of-denominators) for KPI cards and leaderboards.
   * Omitted defaults to monthly bucketing on the backend.
   */
  period?: 'day' | 'week' | 'month' | 'all';
};

export interface SORMetricBodyItem {
  id: string;
  group: string;
  name: string;
  dimensions: SORMetricDimension[];
}

export interface SORMetricResult {
  id: string
  data: {
    value: number | string;
    dimensions: {
      name: string;
      value: unknown;
    }[]
  }[]
}

export interface CitationsParams extends QueryParams {
  type?: string;
  from?: string;
  to?: string;
  country_code?: string;
  search?: string;
  brand_mentioned?: 'branded' | 'own';
  competitor_brand?: string;
  topic?: string;
  model?: string;
}

export interface PromptsParams extends QueryParams {
  topic?: string;
  from?: string;
  to?: string;
  country_code?: string;
  search?: string;
  signal?: 'WINNING' | 'WATCH' | 'GAP';
  sentiment?: 'POSITIVE' | 'NEGATIVE' | 'NEUTRAL';
  model?: string;
  /** Comma-separated multi-select: any of 'own', 'third'. */
  citation_source?: string;
  /** Comma-separated competitor names for "cites competitor's site". */
  competitor_brands?: string;
}

export interface Citation {
  citation_url: string;
  type: string | null;
  topics_covered: string[] | null;
  prompts_covered: string;
  responses_covered: string;
  brand_mentioned: boolean;
  competitors_mentioned: string[] | null;
}

export interface CitationResponse {
  citations: Citation[];
  /** Total AI response samples in scope — denominator for the "N of M" Frequency. */
  frequency_total: number;
  /** Grand total of all frequency values across every matching citation — footer denominator. */
  frequency_sum_total: number;
  pagination: Pagination;
}

export interface Prompt {
  uuid: string;
  prompt: string;
  topic: string | null;
  visibility_score: string | null;
  average_position: string | null;
  models_mentioned: string;
  models_ranked: string;
  models_total: string;
  overall_sentiment: 'POSITIVE' | 'NEGATIVE' | 'NEUTRAL';
  citation_urls: string[] | null;
  citation_count: string;
  signal: 'WINNING' | 'WATCH' | 'GAP' | 'INSUFFICIENT_DATA' | null;
}

export interface PromptResponse {
  prompts: Prompt[];
  pagination: Pagination
}

export type CitationSource =
  | 'OWNED'
  | 'COMPETITOR_OWNED'
  | 'RETAIL'
  | 'REVIEW_PLATFORMS'
  | 'COMMUNITY'
  | 'EDITORIAL'
  | 'EXPERT'
  | 'VIDEO'
  | 'SOCIAL';

export interface PromptDetailCitation {
  url: string;
  source: CitationSource;
  brands: string[];
}

export interface PromptDetailModel {
  model: string;
  date: string;
  brand_mention_count: number;
  /** Whether the representative (displayed) sample mentions the brand. */
  representative_brand_mention: boolean;
  /** The representative (displayed) sample's rank, or null when not ranked. */
  representative_position: number | null;
  total_samples: number;
  overall_sentiment: 'POSITIVE' | 'NEGATIVE' | 'NEUTRAL' | null;
  average_position: number;
  response_text: string;
  brands_mentioned: string[];
  total_citations: number;
  brand_citations: number;
  citations: PromptDetailCitation[];
}

export interface PromptDetailResponse {
  uuid: string;
  models: PromptDetailModel[];
}

export interface BlogSection {
  id: string;
  content: string;
  sequence_number: number;
  heading_level?: number;
}

export interface BlogSectionCollection {
  sections: BlogSection[];
}

export interface BlogPostRefreshTask {
  id: string;
  key: string;
  extracted_content: string;
  refreshed_content: string;
  original_content: BlogSectionCollection;
  agent_generated_content: BlogSectionCollection;
  user_modified_content: BlogSectionCollection;
  metadata: Record<string, unknown>;
  status: 'PENDING' | 'IN_PROGRESS' | 'FAILED' | 'WAITING_FOR_APPROVAL' | 'COMPLETED';
}

export interface BlogPostRefreshTaskResponse {
  blog_post_refresh_tasks: BlogPostRefreshTask[];
}

export type BlogPostOutlineTaskStatus =
  | 'PENDING'
  | 'IN_PROGRESS'
  | 'FAILED'
  | 'WAITING_FOR_APPROVAL'
  | 'COMPLETED';

export interface BlogPostOutlineTask {
  id: string;
  key: string;
  agent_generated_content: BlogSectionCollection | null;
  user_modified_content: BlogSectionCollection | null;
  metadata: Record<string, unknown>;
  status: BlogPostOutlineTaskStatus;
}

export interface BlogPostOutlineTaskResponse {
  blog_post_outline_tasks: BlogPostOutlineTask[];
}

/**
 * Payload for creating a new insight
 * Matches the schema from InsightCreateRequest.yml
 */
interface InsightCreatePayload {
  insight: {
    source: 'CHAT';
    workflow_execution_id: string;
    display_name: string;
    blocks: unknown[];
    schedule: {
      cron: string;
      timezone: string;
    };
    data_range: {
      type: 'relative' | 'absolute';
      unit?: 'DAYS' | 'WEEKS' | 'MONTHS';
      value: number;
    };
  };
}

/**
 * Payload for the HTML variant of {@link createInsight}. When `html_content`
 * is supplied the server stores the insight as `type=HTML` in `READY` state
 * without triggering the insight-generation workflow. Used by the Report
 * Studio "Create a report" flow to spin up a bare HTML scaffold that the
 * chat agent then iterates on.
 */
interface HtmlInsightCreatePayload {
  insight: {
    display_name: string;
    html_content: string;
  };
}

const BASE_URL = getDomainConfig().apiBaseUrl;

// Common utility function to construct headers
const constructHeaders = () => {
  const headers = new Headers();
  headers.append("Content-Type", "application/json");
  return headers;
}

// Common utility function to construct payload
const getPayload = (headers: Headers, method: HttpMethodType = 'GET', body: object | null = null) => {
  const payload: {
    method: HttpMethodType,
    headers: Headers,
    body?: string,
  } = {
    method,
    headers,
  };

  if (method !== 'GET' && body) {
    payload.body = JSON.stringify(body);
  } else if (method === 'DELETE') {
    headers.delete('Content-Type');
  }

  return payload;
}

// Generic SSE-like event stream processor
export interface ProcessEventStreamEvent {
  [key: string]: unknown;
}

export type ProcessEventStreamOnEvent = (event: ProcessEventStreamEvent) => void;

export async function processEventStream(
  response: Response,
  onEvent: ProcessEventStreamOnEvent,
  signal?: AbortSignal
): Promise<void> {
  if (!response.ok || !response.body) {
    throw new Error(`Event stream failed with status ${response.status}`);
  }
  const reader: ReadableStreamDefaultReader<Uint8Array> = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  while (true) {
    const { done, value }: { done: boolean; value?: Uint8Array } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const parts: string[] = buffer.split('\n\n');
    buffer = parts.pop() ?? '';
    for (const part of parts) {
      // Check abort before processing each SSE message to prevent stale
      // event dispatch after the stream has been cancelled.
      if (signal?.aborted) break;
      const lines: string[] = part.split('\n');
      for (const line of lines) {
        const match: RegExpMatchArray | null = line.match(/^data:\s*(.*)$/);
        if (match) {
          try {
            const evt: ProcessEventStreamEvent = JSON.parse(match[1]);
            onEvent(evt);
          } catch {
            // Ignore malformed lines
          }
        }
      }
    }
    if (signal?.aborted) break;
  }
}

// API to list all installed agents
const listAgentInstances = async () => {
  try {
    const response = await fetch(
      `${BASE_URL}/api/v1/agent_instance`,
      getPayload(constructHeaders())
    );

    if (response.ok) {
      const res = await response.json();
      return res;
    }
    throwApiError('Error fetching installed agents', response.status);
  } catch (error) {
    // Re-throw ApiError to preserve status code for retry logic
    if (error instanceof ApiError) {
      throw error;
    }
    logger(error as Error, { context: 'listAgentInstances: Error fetching installed agents' });
    throw new Error('Error fetching installed agents');
  }
};

// API to list all available agents
const listAgents = async () => {
  try {
    const response = await fetch(
      `${BASE_URL}/api/v1/agent`,
      getPayload(constructHeaders())
    );

    if (response.ok) {
      const res = await response.json();
      return res;
    }
    throw new Error('Error fetching all agents');
  } catch (error) {
    logger(error as Error, { context: 'listAgents: Error fetching all agents' });
    throw new Error('Error fetching all agents');
  }
};

// API to create new credential
const createNewCredential = async (payload: CreateCredentialPayload) => {
  try {
    const response = await fetch(
      `${BASE_URL}/api/v1/credential`,
      getPayload(constructHeaders(), "POST", payload)
    );

    if (response.ok) {
      const res = await response.json();
      return res;
    }
    throw new Error('Error creating new credential');
  } catch (error) {
    logger(error as Error, { context: 'createNewCredential: Error creating new credential' });
    throw new Error('Error creating new credential');
  }
};

// API to pause or resume a credential (PATCH `status` on the existing
// patch endpoint — pause/resume are not separate routes).
const patchCredentialStatus = async (
  credentialId: string,
  status: 'ACTIVE' | 'PAUSED'
) => {
  try {
    const response = await fetch(
      `${BASE_URL}/api/v1/credential/${encodeURIComponent(credentialId)}`,
      getPayload(constructHeaders(), 'PATCH', { credential: { status } })
    );

    if (response.ok) {
      return await response.json();
    }
    throwApiError('Error updating credential status', response.status);
  } catch (error) {
    if (error instanceof ApiError) throw error;
    logger(error as Error, { context: 'patchCredentialStatus: Error updating credential status' });
    throw new Error('Error updating credential status');
  }
};

// API to delete a credential (server revokes the upstream OAuth grant
// before destroying the row).
const deleteCredential = async (credentialId: string): Promise<void> => {
  try {
    const response = await fetch(
      `${BASE_URL}/api/v1/credential/${encodeURIComponent(credentialId)}`,
      getPayload(constructHeaders(), 'DELETE')
    );
    if (response.ok || response.status === 204) return;
    throwApiError('Error deleting credential', response.status);
  } catch (error) {
    if (error instanceof ApiError) throw error;
    logger(error as Error, { context: 'deleteCredential: Error deleting credential' });
    throw new Error('Error deleting credential');
  }
};

// API to reauthorize existing credential
const patchCredential = async (credentialId: string, payload: OauthCredentials | ApiKeyCredentials | PatchCredentialCustomData) => {
  try {
    const response = await fetch(
      `${BASE_URL}/api/v1/credential/${credentialId}`,
      getPayload(constructHeaders(), "PATCH", payload)
    );

    if (response.ok) {
      const res = await response.json();
      return res;
    }
    throw new Error('Error updating credential');
  } catch (error) {
    logger(error as Error, { context: 'patchCredential: Error updating credential' });
    throw new Error('Error updating credential');
  }
};

// API to get tenant details
const getTenantDetails = async () => {
  try {
    const response = await fetch(
      `${BASE_URL}/api/v1/session/tenant`,
      getPayload(constructHeaders())
    );

    if (response.ok) {
      const res = await response.json();
      return res;
    }
    throwApiError('Error fetching tenant details', response.status);
  } catch (error) {
    // Re-throw ApiError to preserve status code for retry logic
    if (error instanceof ApiError) {
      throw error;
    }
    logger(error as Error, { context: 'getTenantDetails: Error fetching tenant details' });
    throw new Error('Error fetching tenant details');
  }
};

// API to fetch list of existing credentials
interface FetchAllCredentialResponse {
  credentials: ConnectionsCardData[];
}

const fetchAllCredential = async (): Promise<FetchAllCredentialResponse> => {
  try {
    const response = await fetch(
      `${BASE_URL}/api/v1/credential`,
      getPayload(constructHeaders(), "GET")
    );

    if (response.ok) {
      const res = await response.json() as FetchAllCredentialResponse;
      return res;
    }
    throw new Error('Error fetching credential');
  } catch (error) {
    logger(error as Error, { context: 'fetchCredential: Error fetching credential' });
    throw new Error('Error fetching credential');
  }
};

// API to update tenant settings
const updateTenantSettings = async (settings: TenantSettings) => {
  try {
    const response = await fetch(
      `${BASE_URL}/api/v1/session/tenant`,
      getPayload(constructHeaders(), "PATCH", {
        tenant: { settings }
      })
    );

    if (response.ok) {
      const res = await response.json();
      return res;
    }
    throw new Error('Error updating tenant settings');
  } catch (error) {
    logger(error as Error, { context: 'updateTenantSettings: Error updating tenant settings' });
    throw new Error('Error updating tenant settings');
  }
};

// API to mark tenant as onboarded
const updateTenantOnboarded = async () => {
  try {
    const response = await fetch(
      `${BASE_URL}/api/v1/session/tenant`,
      getPayload(constructHeaders(), "PATCH", {
        tenant: { is_onboarded: true }
      })
    );

    if (response.ok) {
      const res = await response.json();
      return res;
    }
    throw new Error('Error marking tenant as onboarded');
  } catch (error) {
    logger(error as Error, { context: 'updateTenantOnboarded: Error marking tenant as onboarded' });
    throw new Error('Error marking tenant as onboarded');
  }
};

// API to create new agent instance
const createAgentInstance = async (payload: CreateAgentInstancePayload) => {
  try {
    const response = await fetch(
      `${BASE_URL}/api/v1/agent_instance`,
      getPayload(constructHeaders(), "POST", payload)
    );

    if (response.ok) {
      const res = await response.json();
      return res;
    }
    throw new Error('Error creating agent instance');
  } catch (error) {
    logger(error as Error, { context: 'createAgentInstance: Error creating agent instance' });
    throw new Error('Error creating agent instance');
  }
};

const installAgent = async (agentShortName: string): Promise<{ status: string }> => {
  try {
    const response = await fetch(
      `${BASE_URL}/api/v1/internal/agent_setup/install/${agentShortName}`,
      getPayload(constructHeaders(), "POST", {})
    );

    if (response.ok) {
      return await response.json();
    }
    throw new Error('Error installing agent');
  } catch (error) {
    logger(error as Error, { context: 'installAgent: Error installing agent' });
    throw new Error('Error installing agent');
  }
};

// API to update agent instance
const updateAgentInstance = async (agentInstanceId: string, payload: UpdateAgentInstancePayload) => {
  try {
    const response = await fetch(
      `${BASE_URL}/api/v1/agent_instance/${agentInstanceId}`,
      getPayload(constructHeaders(), "PATCH", payload)
    );

    if (response.ok) {
      const res = await response.json();
      return res;
    }
    throw new Error('Error updating agent instance');
  } catch (error) {
    logger(error as Error, { context: 'updateAgentInstance: Error updating agent instance' });
    throw new Error('Error updating agent instance');
  }
};

// API to list schedules for a workflow instance
const listWorkflowInstanceSchedules = async (workflowInstanceId: string) => {
  try {
    const response = await fetch(
      `${BASE_URL}/api/v1/workflow_instance/${workflowInstanceId}/workflow_instance_schedule`,
      getPayload(constructHeaders())
    );
    if (response.ok) {
      const res = await response.json();
      return res;
    }
    throw new Error('Error listing workflow instance schedules');
  } catch (error) {
    logger(error as Error, { context: 'listWorkflowInstanceSchedules: Error listing schedules' });
    throw new Error('Error listing workflow instance schedules');
  }
};

// API to create a schedule for a workflow instance
const createWorkflowInstanceSchedule = async (workflowInstanceId: string, cron: string, timezone: string) => {
  try {
    const response = await fetch(
      `${BASE_URL}/api/v1/workflow_instance/${workflowInstanceId}/workflow_instance_schedule`,
      getPayload(constructHeaders(), 'POST', {
        workflow_instance_schedule: {
          cron,
          timezone,
          trigger_data: {}
        }
      })
    );
    if (response.ok) {
      const res = await response.json();
      return res;
    }
    throw new Error('Error creating workflow instance schedule');
  } catch (error) {
    logger(error as Error, { context: 'createWorkflowInstanceSchedule: Error creating schedule' });
    throw new Error('Error creating workflow instance schedule');
  }
};

const fetchAEOSchedule = async (): Promise<{
  cron: string;
  cron_timezone: string;
  last_refreshed_at: string | null;
  next_refresh_at: string | null;
}> => {
  const response = await fetch(
    `${BASE_URL}/api/v1/sor/aeo/schedule`,
    getPayload(constructHeaders(), 'GET'),
  );
  if (!response.ok) {
    const err = new Error(`Failed to fetch AEO schedule (${response.status})`);
    logger(err, { context: 'fetchAEOSchedule', status: response.status });
    throw err;
  }
  return response.json();
};

const updateAEOSchedule = async (
  cron: string,
  cron_timezone: string,
): Promise<{ cron: string; cron_timezone: string }> => {
  const response = await fetch(
    `${BASE_URL}/api/v1/sor/aeo/schedule`,
    getPayload(constructHeaders(), 'PATCH', { cron, cron_timezone }),
  );
  if (!response.ok) {
    const err = new Error(`Failed to update AEO schedule (${response.status})`);
    logger(err, { context: 'updateAEOSchedule', status: response.status });
    throw err;
  }
  return response.json();
};

// API to update an existing workflow instance schedule
const updateWorkflowInstanceSchedule = async (
  workflowInstanceId: string,
  scheduleId: string,
  cron: string,
  timezone: string
) => {
  try {
    const response = await fetch(
      `${BASE_URL}/api/v1/workflow_instance/${workflowInstanceId}/workflow_instance_schedule/${scheduleId}`,
      getPayload(constructHeaders(), 'PATCH', {
        workflow_instance_schedule: {
          cron,
          timezone,
          trigger_data: {}
        }
      })
    );
    if (response.ok) {
      const res = await response.json();
      return res;
    }
    throw new Error('Error updating workflow instance schedule');
  } catch (error) {
    logger(error as Error, { context: 'updateWorkflowInstanceSchedule: Error updating schedule' });
    throw new Error('Error updating workflow instance schedule');
  }
};

// API to trigger workflow
const triggerWorkflow = async (payload: TriggerWorkflowPayload) => {
  try {
    const response = await fetch(
      `${BASE_URL}/api/v1/internal/trigger_workflow`,
      getPayload(constructHeaders(), "POST", payload)
    );

    if (response.ok) {
      const res = await response.json();
      return res;
    }
    throw new Error('Error triggering workflow');
  } catch (error) {
    logger(error as Error, { context: 'triggerWorkflow: Error triggering workflow' });
    throw new Error('Error triggering workflow');
  }
};

// get the execution status of the triggered workflow instance
const getWorkFlowInstanceExecutionStatus = async (baseUrl: string, workflow_instance_id: string, workflow_execution_id: string) => {
  try {
    const response = await fetch(`${baseUrl}/api/v1/workflow_instance/${encodeURIComponent(workflow_instance_id)}/workflow_execution/${encodeURIComponent(workflow_execution_id)}`, getPayload(constructHeaders()));
    if (response.ok) {
      const res = await response.json();
      return res;
    }
    throw new Error('Error fetching workflow execution status');
  } catch (error) {
    logger(error as Error, { context: 'getWorkFlowInstanceExecutionStatus: Error fetching workflow execution status' });
    throw new Error('Error fetching workflow execution status');
  }
};

// Resources attached to a chat workflow execution. Reuses the single-execution
// GET (the response already serializes `workflow_execution_resources`) — no
// dedicated endpoint, and deliberately not the polling status hook.
const getWorkflowExecutionResources = async (
  workflow_instance_id: string,
  workflow_execution_id: string
): Promise<WorkflowExecutionResourceDTO[]> => {
  try {
    const response = await fetch(
      `${BASE_URL}/api/v1/workflow_instance/${encodeURIComponent(workflow_instance_id)}/workflow_execution/${encodeURIComponent(workflow_execution_id)}`,
      getPayload(constructHeaders())
    );
    if (response.ok) {
      const res = await response.json();
      return (res?.workflow_execution?.workflow_execution_resources ?? []) as WorkflowExecutionResourceDTO[];
    }
    throw new Error('Error fetching workflow execution resources');
  } catch (error) {
    logger(error as Error, { context: 'getWorkflowExecutionResources: Error fetching workflow execution resources' });
    throw new Error('Error fetching workflow execution resources');
  }
};

// Attach resources to an existing chat workflow execution. The backend
// persists the workflow_execution_resource rows AND syncs each insight into
// the tenant sandbox before responding, so a send awaited on this call is
// guaranteed the agent's read_file finds the file.
const attachWorkflowExecutionResources = async (
  workflow_instance_id: string,
  workflow_execution_id: string,
  resources: WorkflowExecutionResourceInput[]
): Promise<WorkflowExecutionResourceDTO[]> => {
  try {
    const response = await fetch(
      `${BASE_URL}/api/v1/workflow_instance/${encodeURIComponent(workflow_instance_id)}/workflow_execution/${encodeURIComponent(workflow_execution_id)}/workflow_execution_resource`,
      getPayload(constructHeaders(), 'POST', { resources })
    );
    if (response.ok) {
      const res = await response.json();
      return (res?.workflow_execution_resources ?? []) as WorkflowExecutionResourceDTO[];
    }
    throw new Error('Error attaching workflow execution resources');
  } catch (error) {
    logger(error as Error, { context: 'attachWorkflowExecutionResources: Error attaching workflow execution resources' });
    throw new Error('Error attaching workflow execution resources');
  }
};

// API to trigger chat workflow (returns workflow_execution.id)
const triggerChatWorkflow = async (payload: TriggerChatWorkflowPayload) => {
  try {
    const response = await fetch(
      `${BASE_URL}/api/v1/internal/trigger_chat_workflow`,
      getPayload(constructHeaders(), 'POST', payload)
    );
    if (response.ok) {
      const res = await response.json();
      return res as { workflow_execution: { id: string } };
    }
    throw new Error('Error triggering chat workflow');
  } catch (error) {
    logger(error as Error, { context: 'triggerChatWorkflow: Error triggering chat workflow' });
    throw new Error('Error triggering chat workflow');
  }
};

// Chat stream with workflow execution (Server-Sent Events-like stream)
// It parses lines starting with `data: {json}` as individual events.
const chatWithWorkflowStream = async (
  params: ChatWithWorkflowStreamParams
) => {
  const { baseUrl = BASE_URL, workflow_instance_id, workflow_execution_id, body, signal, onEvent, onError, onDone } = params;
  try {
    const response = await fetch(
      `${baseUrl}/api/v1/workflow_instance/${encodeURIComponent(workflow_instance_id)}/workflow_execution/${encodeURIComponent(workflow_execution_id)}/chat`,
      { ...getPayload(constructHeaders(), 'POST', body), signal }
    );
    await processEventStream(response, onEvent, signal);
    onDone?.();
  } catch (e) {
    const err = e as Error;
    onError?.(err);
    logger(err, { context: 'chatWithWorkflowStream: streaming error' });
    throw err;
  }
};

// Execute any pending tool calls on the last chat message. Pre-resolved
// outputs (e.g. produced by interactive UI tools in the browser) are sent in
// `tool_call_outputs`; the server executes the remaining tool calls. Returns
// 204 No Content — to continue the conversation, follow this with a CONTINUE
// chat call.
const executePendingTools = async (params: ExecutePendingToolsParams): Promise<void> => {
  const { baseUrl = BASE_URL, workflow_instance_id, workflow_execution_id, tool_call_outputs, signal } = params;
  const body = tool_call_outputs && tool_call_outputs.length > 0
    ? { tool_call_outputs }
    : {};
  try {
    const response = await fetch(
      `${baseUrl}/api/v1/workflow_instance/${encodeURIComponent(workflow_instance_id)}/workflow_execution/${encodeURIComponent(workflow_execution_id)}/execute_tools`,
      { ...getPayload(constructHeaders(), 'POST', body), signal }
    );
    if (!response.ok) {
      throw new Error(`executePendingTools failed with status ${response.status}`);
    }
  } catch (e) {
    const err = e as Error;
    logger(err, { context: 'executePendingTools: error' });
    throw err;
  }
};

// API to list all workflow instances
const listAllWorkflowInstances = async (baseUrl: string) => {
  try {
    const response = await fetch(`${baseUrl}/api/v1/workflow_instance`, getPayload(constructHeaders()));
    if (response.ok) {
      const res = await response.json();
      return res;
    }
    throw new Error('Error fetching workflow instances');
  } catch (error) {
    logger(error as Error, { context: 'listAllWorkflowInstances: Error fetching workflow instances' });
    throw new Error('Error fetching workflow instances');
  }
};

// API to list all workflow executions for a workflow instance
const listAllWorkflowExecutions = async (baseUrl: string, workflowInstanceId: string, params?: QueryParams, signal?: AbortSignal) => {
  try {
    const queryParams = params ?
      `?${new URLSearchParams(params as Record<string, string>).toString()}` : '';
    const response = await fetch(`${baseUrl}/api/v1/workflow_instance/${workflowInstanceId}/workflow_execution${queryParams}`, { ...getPayload(constructHeaders()), signal });
    if (response.ok) {
      const res = await response.json();
      return res;
    }
    throwApiError('Error fetching workflow executions', response.status);
  } catch (error) {
    // Re-throw ApiError to preserve status code for retry logic
    if (error instanceof ApiError) {
      throw error;
    }
    if ((error as Error)?.name === 'AbortError') {
      throw error;
    }
    logger(error as Error, { context: 'listAllWorkflowExecutions: Error fetching workflow executions' });
    throw new Error('Error fetching workflow executions');
  }
}

// API to get workflow execution state
const getWorkflowExecutionState = async (baseUrl: string, workflow_instance_id: string, workflow_execution_id: string) => {
  try {
    const response = await fetch(
      `${baseUrl}/api/v1/workflow_instance/${encodeURIComponent(workflow_instance_id)}/workflow_execution/${encodeURIComponent(workflow_execution_id)}/workflow_execution_state`,
      getPayload(constructHeaders())
    );
    if (response.ok) {
      const res = await response.json();
      return res;
    }
    throw new Error('Error fetching workflow execution state');
  } catch (error) {
    logger(error as Error, { context: 'getWorkflowExecutionState: Error fetching workflow execution state' });
    throw new Error('Error fetching workflow execution state');
  }
};


// API to list all approvals
const listAllApprovals = async (baseUrl: string, params?: ApprovalQueryParams) => {
  try {
    const queryParams = params ?
      `?${new URLSearchParams(params as Record<string, string>).toString()}` : '';

    const response = await fetch(`${baseUrl}/api/v1/approval${queryParams}`, getPayload(constructHeaders()));

    if (response.ok) {
      const res = await response.json();
      return res;
    }
    throw new Error('Error fetching approval list');
  } catch (error) {
    logger(error as Error, { context: 'listAllApprovals: Error fetching approval list' });
    throw new Error('Error fetching approval list');
  }
};

// API to create new child tenant
const createTenant = async (payload: TenantPayload) => {
  try {
    const response = await fetch(`${BASE_URL}/api/v1/tenant`, getPayload(constructHeaders(), "POST", payload));

    if (response.ok) {
      const res = await response.json();
      return res;
    }
    throw new Error('Error creating new tenant');
  } catch (error) {
    logger(error as Error, { context: 'createTenant: Error creating new tenant' });
    throw new Error('Error creating new tenant');
  }
};

// API to create new tenant variable
const createNewTenantVariable = async (tenantId: string, payload: TenantVariablePayload) => {
  try {
    const response = await fetch(
      `${BASE_URL}/api/v1/tenant/${encodeURIComponent(tenantId)}/tenant_variable`,
      getPayload(constructHeaders(), "POST", payload)
    );

    if (response.ok) {
      const res = await response.json();
      return res;
    }
    throw new Error('Error creating new tenant variable');
  } catch (error) {
    logger(error as Error, { context: 'createNewTenantVariable: Error creating new tenant variable' });
    throw new Error('Error creating new tenant variable');
  }
};

// API to update approval status
const updateApproval = async (baseUrl: string, approvalId: string, approvalStatus: string) => {
  try {
    const response = await fetch(`${baseUrl}/api/v1/approval/${approvalId}`, getPayload(constructHeaders(), "PATCH", {
      "approval": {
        "status": approvalStatus
      }
    }));
    if (response.ok) {
      const res = await response.json();
      return res;
    }
    throw new Error('Error updating approval status');
  } catch (error) {
    logger(error as Error, { context: 'updateApproval: Error updating approval status' });
    throw new Error('Error updating approval status');
  }
};

// API to list all approval items
const listAllApprovalItems = async (baseUrl: string, approvalId: string) => {
  try {
    const response = await fetch(`${baseUrl}/api/v1/approval/${approvalId}/approval_item`, getPayload(constructHeaders()));
    if (response.ok) {
      const res = await response.json();
      return res;
    }
    throw new Error('Error fetching approval items');
  } catch (error) {
    logger(error as Error, { context: 'listAllApprovalItems: Error fetching approval items' });
    throw new Error('Error fetching approval items');
  }
};

// API to list all variables in a category
const listAllVariablesInCategory = async (baseUrl: string, variableCategory: string) => {
  try {
    const response = await fetch(`${baseUrl}/api/v1/variable_category/${variableCategory}/variable`, getPayload(constructHeaders()));
    if (response.ok) {
      const res = await response.json();
      return res;
    }
    throw new Error('Error listing variables in the category');
  } catch (error) {
    logger(error as Error, { context: 'listAllVariablesInCategory: Error listing variables in the category' });
    throw new Error('Error listing variables in the category');
  }
};

// API to update approval item status
const updateApprovalItem = async (
  baseUrl: string,
  approvalId: string,
  approvalItemId: string,
  approvalStatus: string,
  reviewedData?: Record<string, unknown>,
  feedbackText?: string
) => {
  try {
    const payload: Record<string, unknown> = {
      "approval_item": {
        "status": approvalStatus
      }
    };
    // If reviewedData is provided, add it to the payload
    if (reviewedData) {
      payload["approval_item"]["reviewed_data"] = reviewedData;
    }

    // If feedbackText is provided, add it to the payload
    if (feedbackText) {
      payload["approval_item"]["feedback_text"] = feedbackText;
    }

    const response = await fetch(
      `${baseUrl}/api/v1/approval/${approvalId}/approval_item/${approvalItemId}`,
      getPayload(constructHeaders(), "PATCH", payload)
    );

    if (response.ok) {
      const res = await response.json();
      return res;
    }
    throw new Error('Error updating approval item');
  } catch (error) {
    logger(error as Error, { context: 'updateApprovalItem: Error updating approval item' });
    throw new Error('Error updating approval item');
  }
};

// API to execute an app action
const executeAppAction = async (
  baseUrl: string,
  app_name: string,
  action_name: string,
  credentials: unknown = [],
  data = [{}],
  input?: Array<{
    data: Record<string, unknown>;
    binaries?: Record<string, { content_base_64: string; file_name: string; mime_type: string }>;
  }>,
  // When true, the backend refuses write actions for this call. Set by the
  // sandboxed HTML-report bridge, whose embedded `app.execute` calls run
  // unattended at view-time and must stay read-only.
  readOnly?: boolean
) => {
  try {
    const appExecution: Record<string, unknown> = {
      app_name,
      action_name,
      credentials
    };

    if (readOnly) {
      appExecution.read_only = true;
    }

    // Use new input format if provided, otherwise fall back to old data format
    if (input) {
      appExecution.input = input;
    } else {
      appExecution.data = data;
    }

    const response = await fetch(`${baseUrl}/api/v1/internal/execute_app`, getPayload(constructHeaders(), "POST", {
      app_execution: appExecution
    }));

    if (response.ok) {
      const res = await response.json();
      return res;
    }

    const errorBody = await response.json().catch(() => null);
    const errorMessage = errorBody?.message || 'Error executing the app action';
    throw new Error(errorMessage);
  } catch (error) {
    logger(error as Error, { context: 'executeAppAction: Error executing the app action' });
    throw error instanceof Error ? error : new Error('Error executing the app action');
  }
};

export type WorkflowExecutionCheckpointStatus =
  | 'IN_PROGRESS'
  | 'COMPLETED'
  | 'FAILED'
  | 'APPROVAL_PENDING';

export interface WorkflowExecutionCheckpoint {
  node_id: string;
  label: string;
  status: WorkflowExecutionCheckpointStatus;
}

export interface WorkflowExecutionCheckpointsResponse {
  workflow_execution_checkpoints: WorkflowExecutionCheckpoint[];
}

const fetchWorkflowExecutionCheckpoints = async (
  workflowInstanceId: string,
  workflowExecutionId: string
): Promise<WorkflowExecutionCheckpointsResponse> => {
  try {
    const url = `${BASE_URL}/api/v1/workflow_instance/${encodeURIComponent(workflowInstanceId)}/workflow_execution/${encodeURIComponent(workflowExecutionId)}/workflow_execution_checkpoint`;
    const response = await fetch(url, getPayload(constructHeaders()));

    if (response.ok) {
      return await response.json();
    }
    throw new Error('Error fetching workflow execution checkpoints');
  } catch (error) {
    logger(error as Error, { context: 'fetchWorkflowExecutionCheckpoints: Error fetching workflow execution checkpoints' });
    throw new Error('Error fetching workflow execution checkpoints');
  }
};

// API to fetch workflow execution states for specific nodes
const fetchWorkflowExecutionStates = async (baseUrl: string, workflow_instance_id: string, workflow_execution_id: string, node_ids: string[]) => {
  try {
    const url = `${baseUrl}/api/v1/workflow_instance/${encodeURIComponent(workflow_instance_id)}/workflow_execution/${encodeURIComponent(workflow_execution_id)}/workflow_execution_state?${node_ids.map(id => `node_id=${encodeURIComponent(id)}`).join('&')}`;

    const response = await fetch(url, getPayload(constructHeaders()));

    if (response.ok) {
      const res = await response.json();
      return res;
    }
    throw new Error('Error fetching workflow execution states');
  } catch (error) {
    logger(error as Error, { context: 'fetchWorkflowExecutionStates: Error fetching workflow execution states' });
    throw new Error('Error fetching workflow execution states');
  }
};

// API to list all tenant variables for a tenant
const listAllTenantVariables = async (tenantId: string): Promise<TenantVariableRecord[]> => {
  try {
    const response = await fetch(`${BASE_URL}/api/v1/tenant/${encodeURIComponent(tenantId)}/tenant_variable`);
    if (response.ok) {
      const data = await response.json();
      return data?.tenant_variables || [];;
    }
    throw new Error('Error listing tenant variables');
  } catch (error) {
    logger(error as Error, { context: 'listAllTenantVariables: Error listing tenant variables' });
    throw new Error('Error listing tenant variables');
  }
}

// API to update a tenant variable
const updateTenantVariable = async (tenantId: string, tenantVariableId: string, value: string) => {
  try {
    const response = await fetch(
      `${BASE_URL}/api/v1/tenant/${encodeURIComponent(tenantId)}/tenant_variable/${encodeURIComponent(tenantVariableId)}`,
      getPayload(constructHeaders(), "PATCH", {
        tenant_variable: { value }
      })
    );

    if (response.ok) {
      const res = await response.json();
      return res;
    }
    throw new Error('Error updating tenant variable');
  } catch (error) {
    logger(error as Error, { context: 'updateTenantVariable: Error updating tenant variable' });
    throw new Error('Error updating tenant variable');
  }
};

// API to manage key-value store (store, get, delete, list)
const manageKeyVal = async (payload: KeyValPayload) => {
  try {
    const response = await fetch(
      `${BASE_URL}/api/v1/key_val`,
      getPayload(constructHeaders(), "POST", payload)
    );

    if (response.ok) {
      const res = await response.json();
      return res;
    }
    throw new Error(`Error performing key-value ${payload.action} operation`);
  } catch (error) {
    logger(error as Error, { context: `manageKeyVal: Error performing key-value ${payload.action} operation` });
    throw error;
  }
};

// API to fetch all available apps
const fetchAllApps = async (): Promise<App[]> => {
  try {
    const response = await fetch(
      `${BASE_URL}/api/v1/app`,
      getPayload(constructHeaders())
    );

    if (response.ok) {
      const data: AppsResponse = await response.json();
      return data.apps || [];
    }
    throw new Error('Error fetching apps');
  } catch (error) {
    logger(error as Error, { context: 'fetchAllApps: Error fetching available apps' });
    throw new Error('Error fetching apps');
  }
};

const fetchAppTools = async (appShortName: string): Promise<PublicAppTool[]> => {
  try {
    const response = await fetch(
      `${BASE_URL}/api/v1/app/${encodeURIComponent(appShortName)}/tools`,
      getPayload(constructHeaders())
    );

    if (response.ok) {
      const data: { tools?: PublicAppTool[] } = await response.json();
      return data.tools ?? [];
    }
    throw new Error('Error fetching app tools');
  } catch (error) {
    logger(error as Error, { context: `fetchAppTools: Error fetching tools for app ${appShortName}` });
    throw new Error('Error fetching app tools');
  }
};

// API to fetch all insights
const fetchAllInsights = async (): Promise<Insight[]> => {
  try {
    const response = await fetch(
      `${BASE_URL}/api/v1/insight`,
      getPayload(constructHeaders())
    );

    if (response.ok) {
      const data: InsightsResponse = await response.json();
      return data.insights || [];
    }
    throw new Error('Error fetching insights');
  } catch (error) {
    logger(error as Error, { context: 'fetchAllInsights: Error fetching insights' });
    throw new Error('Error fetching insights');
  }
};

// API to fetch insight reports for a specific insight
const fetchInsightReports = async (insightId: string): Promise<InsightReport[]> => {
  try {
    const response = await fetch(
      `${BASE_URL}/api/v1/insight/${insightId}/insight_report?sort_by=created_at&sort_order=desc`,
      getPayload(constructHeaders())
    );

    if (response.ok) {
      const data: InsightReportsResponse = await response.json();
      return data.insight_reports || [];
    }
    throw new Error('Error fetching insight reports');
  } catch (error) {
    logger(error as Error, { context: 'fetchInsightReports: Error fetching insight reports' });
    throw new Error('Error fetching insight reports');
  }
};

// Executes a SQL query against the tenant's accessible datasets via the semantic layer.
// Used by the postMessage bridge that brokers requests from sandboxed HTML insight reports.
const queryDataset = async (sql: string): Promise<unknown> => {
  try {
    const response = await fetch(
      `${BASE_URL}/api/v1/dataset/query`,
      getPayload(constructHeaders(), 'POST', { sql })
    );

    if (response.ok) {
      const data: { result: unknown } = await response.json();
      return data.result;
    }
    throw new Error('Error querying dataset');
  } catch (error) {
    logger(error as Error, { context: 'queryDataset: Error querying dataset' });
    throw new Error('Error querying dataset');
  }
};

// Prism semantic-layer client functions. Used by the postMessage bridge to
// expose Cube.js-backed querying to sandboxed HTML reports — the iframe
// never sees credentials or tenant context, the bridge resolves them.

const prismDiscover = async (): Promise<unknown> => {
  try {
    const response = await fetch(
      `${BASE_URL}/api/v1/prism/discover`,
      getPayload(constructHeaders(), 'GET')
    );
    if (response.ok) return (await response.json()).cubes;
    const errorBody = await response.json().catch(() => null);
    throw new Error(errorBody?.message || 'Error discovering cubes');
  } catch (error) {
    logger(error as Error, { context: 'prismDiscover' });
    throw error instanceof Error ? error : new Error('Error discovering cubes');
  }
};

const prismMeta = async (cubes: string[]): Promise<unknown> => {
  try {
    const response = await fetch(
      `${BASE_URL}/api/v1/prism/meta`,
      getPayload(constructHeaders(), 'POST', { cubes })
    );
    if (response.ok) return (await response.json()).cubes;
    const errorBody = await response.json().catch(() => null);
    throw new Error(errorBody?.message || 'Error fetching cube meta');
  } catch (error) {
    logger(error as Error, { context: 'prismMeta' });
    throw error instanceof Error ? error : new Error('Error fetching cube meta');
  }
};

const prismSql = async (sql: string): Promise<unknown> => {
  try {
    const response = await fetch(
      `${BASE_URL}/api/v1/prism/sql`,
      getPayload(constructHeaders(), 'POST', { sql })
    );
    if (response.ok) return (await response.json()).result;
    const errorBody = await response.json().catch(() => null);
    throw new Error(errorBody?.message || 'Error executing Cube SQL');
  } catch (error) {
    logger(error as Error, { context: 'prismSql' });
    throw error instanceof Error ? error : new Error('Error executing Cube SQL');
  }
};

const prismQuery = async (query: Record<string, unknown>): Promise<unknown> => {
  try {
    const response = await fetch(
      `${BASE_URL}/api/v1/prism/query`,
      getPayload(constructHeaders(), 'POST', { query })
    );
    if (response.ok) return (await response.json()).result;
    const errorBody = await response.json().catch(() => null);
    throw new Error(errorBody?.message || 'Error executing Cube query');
  } catch (error) {
    logger(error as Error, { context: 'prismQuery' });
    throw error instanceof Error ? error : new Error('Error executing Cube query');
  }
};

const listGlossary = async (source?: string): Promise<GlossaryEntry[]> => {
  try {
    const url = source
      ? `${BASE_URL}/api/v1/glossary?source=${encodeURIComponent(source)}`
      : `${BASE_URL}/api/v1/glossary`;
    const response = await fetch(url, getPayload(constructHeaders(), 'GET'));
    if (response.ok) {
      const data: { entries: GlossaryEntry[] } = await response.json();
      return data.entries;
    }
    const errorBody = await response.json().catch(() => null);
    throw new Error(errorBody?.message || 'Error fetching glossary');
  } catch (error) {
    logger(error as Error, { context: 'listGlossary' });
    throw error instanceof Error ? error : new Error('Error fetching glossary');
  }
};

// Fetches a single dataset (sync_status, last_synced_at, ready_for_use).
// Used by the postMessage bridge to expose sync state to sandboxed HTML reports.
const getDataset = async (credentialId: string, datasetId: string): Promise<unknown> => {
  try {
    const response = await fetch(
      `${BASE_URL}/api/v1/credential/${credentialId}/dataset/${datasetId}`,
      getPayload(constructHeaders(), 'GET')
    );
    if (response.ok) {
      const data: { dataset: unknown } = await response.json();
      return data.dataset;
    }
    const errorBody = await response.json().catch(() => null);
    throw new Error(errorBody?.message || 'Error fetching dataset');
  } catch (error) {
    logger(error as Error, { context: 'getDataset: Error fetching dataset' });
    throw error instanceof Error ? error : new Error('Error fetching dataset');
  }
};

// Triggers a manual Fivetran sync for a dataset. Returns the dataset with
// sync_status optimistically flipped to SYNC_IN_PROGRESS.
const triggerDatasetSync = async (credentialId: string, datasetId: string): Promise<unknown> => {
  try {
    const response = await fetch(
      `${BASE_URL}/api/v1/credential/${credentialId}/dataset/${datasetId}/sync`,
      getPayload(constructHeaders(), 'POST', {})
    );
    if (response.ok) {
      const data: { dataset: unknown } = await response.json();
      return data.dataset;
    }
    const errorBody = await response.json().catch(() => null);
    throw new Error(errorBody?.message || 'Error triggering dataset sync');
  } catch (error) {
    logger(error as Error, { context: 'triggerDatasetSync: Error triggering dataset sync' });
    throw error instanceof Error ? error : new Error('Error triggering dataset sync');
  }
};

// API to fetch chat history for a workflow execution
const getChatHistory = async (
  baseUrl: string,
  workflowInstanceId: string,
  workflowExecutionId: string,
  params?: ChatHistoryQueryParams
) => {
  try {
    const queryParams = params
      ? `?${new URLSearchParams(params as Record<string, string>).toString()}`
      : '';
    const response = await fetch(
      `${baseUrl}/api/v1/workflow_instance/${encodeURIComponent(workflowInstanceId)}/workflow_execution/${encodeURIComponent(workflowExecutionId)}/chat_history${queryParams}`,
      getPayload(constructHeaders())
    );
    if (response.ok) {
      return await response.json() as ChatHistoryResponse;
    }
    throw new Error('Error fetching chat history');
  } catch (error) {
    logger(error as Error, { context: 'getChatHistory: Error fetching chat history' });
    throw new Error('Error fetching chat history');
  }
};

// API to update behaviour action state for a chat history record
const updateBehaviourActionState = async (
  workflowInstanceId: string,
  workflowExecutionId: string,
  chatHistoryId: string,
  behaviourActionStateId: string,
  state: Record<string, unknown>
) => {
  try {
    const response = await fetch(
      `${BASE_URL}/api/v1/workflow_instance/${encodeURIComponent(workflowInstanceId)}/workflow_execution/${encodeURIComponent(workflowExecutionId)}/chat_history/${encodeURIComponent(chatHistoryId)}/behaviour_action_state/${encodeURIComponent(behaviourActionStateId)}`,
      getPayload(constructHeaders(), 'PUT', {
        behaviour_action_state: state
      } as UpdateBehaviourActionStatePayload)
    );

    if (response.ok) {
      const res = await response.json();
      return res;
    }
    throw new Error('Error updating behaviour action state');
  } catch (error) {
    logger(error as Error, { context: 'updateBehaviourActionState: Error updating behaviour action state' });
    throw new Error('Error updating behaviour action state');
  }
};

// API to fetch SOR metrics
const fetchSORMetrics = async (
  metrics: SORMetricBodyItem[],
  options?: { signal?: AbortSignal }
): Promise<SORMetricResult[]> => {
  try {
    const response = await fetch(
      `${BASE_URL}/api/v1/sor/metric`,
      { ...getPayload(constructHeaders(), "POST", { metrics }), signal: options?.signal }
    );

    if (response.ok) {
      const data = await response.json();
      return data.metrics || [];
    }

    throw new Error('Error computing metrics');
  } catch (error) {
    if ((error as Error)?.name === 'AbortError') throw error;
    logger(error as Error, { context: 'fetchMetrics: Error computing metrics' });
    throw new Error('Error computing metrics');
  }
};

// API to fetch SOR citations
const fetchCitations = async (
  params?: CitationsParams,
  options?: { signal?: AbortSignal }
): Promise<CitationResponse> => {
  try {
    const queryParams = new URLSearchParams(
      Object.entries(params)
        .filter(([, value]) => value !== undefined)
        .map(([key, value]) => [key, String(value)])
    ).toString();

    const response = await fetch(`${BASE_URL}/api/v1/sor/aeo/citation?${queryParams}`,
      { ...getPayload(constructHeaders(), 'GET'), signal: options?.signal }
    );

    if (!response.ok) {
      throw new Error('Failed to fetch citations');
    }

    return response.json();
  } catch (error) {
    if ((error as Error)?.name === 'AbortError') throw error;
    logger(error as Error, { context: 'fetchCitations: Error fetching citations' });
    throw new Error('Error fetching citations');
  }
};

// API to fetch citation presence heatmap data
const fetchCitationPresence = async (
  params?: { from?: string; to?: string; country_code?: string; model?: string },
) => {
  try {
    const queryParams = params
      ? new URLSearchParams(
          Object.entries(params)
            .filter(([, v]) => v !== undefined)
            .map(([k, v]) => [k, String(v)])
        ).toString()
      : '';
    const url = queryParams
      ? `${BASE_URL}/api/v1/sor/aeo/citation/presence?${queryParams}`
      : `${BASE_URL}/api/v1/sor/aeo/citation/presence`;
    const response = await fetch(url, getPayload(constructHeaders(), 'GET'));

    if (!response.ok) {
      throw new Error('Failed to fetch citation presence');
    }

    return response.json();
  } catch (error) {
    logger(error as Error, { context: 'fetchCitationPresence: Error' });
    throw new Error('Error fetching citation presence');
  }
};

// API to fetch citation appearances by source type
const fetchCitationAppearances = async (
  brandName?: string,
  params?: { from?: string; to?: string; country_code?: string; model?: string },
) => {
  try {
    const searchParams = new URLSearchParams();
    if (brandName) searchParams.set('brand_name', brandName);
    if (params?.from) searchParams.set('from', params.from);
    if (params?.to) searchParams.set('to', params.to);
    if (params?.country_code) searchParams.set('country_code', params.country_code);
    if (params?.model) searchParams.set('model', params.model);
    const qs = searchParams.toString();
    const url = qs
      ? `${BASE_URL}/api/v1/sor/aeo/citation/appearances?${qs}`
      : `${BASE_URL}/api/v1/sor/aeo/citation/appearances`;
    const response = await fetch(url, getPayload(constructHeaders(), 'GET'));

    if (!response.ok) {
      throw new Error('Failed to fetch citation appearances');
    }

    return response.json();
  } catch (error) {
    logger(error as Error, { context: 'fetchCitationAppearances: Error' });
    throw new Error('Error fetching citation appearances');
  }
};

export interface CitedDomain {
  domain: string;
  frequency: number;
}

export interface BrandCitedDomains {
  brand: string;
  is_own: boolean;
  domains: CitedDomain[];
}

export interface CitedDomainsResponse {
  own_brand: string;
  brands: BrandCitedDomains[];
}

// API to fetch cited domains rolled up per brand (feeds the Source Authority plot)
const fetchCitedDomains = async (
  brandName?: string,
  params?: { from?: string; to?: string; country_code?: string; model?: string },
): Promise<CitedDomainsResponse> => {
  try {
    const searchParams = new URLSearchParams();
    if (brandName) searchParams.set('brand_name', brandName);
    if (params?.from) searchParams.set('from', params.from);
    if (params?.to) searchParams.set('to', params.to);
    if (params?.country_code) searchParams.set('country_code', params.country_code);
    if (params?.model) searchParams.set('model', params.model);
    const qs = searchParams.toString();
    const url = qs
      ? `${BASE_URL}/api/v1/sor/aeo/citation/domains?${qs}`
      : `${BASE_URL}/api/v1/sor/aeo/citation/domains`;
    const response = await fetch(url, getPayload(constructHeaders(), 'GET'));

    if (!response.ok) {
      throw new Error('Failed to fetch cited domains');
    }

    return response.json();
  } catch (error) {
    logger(error as Error, { context: 'fetchCitedDomains: Error' });
    throw new Error('Error fetching cited domains');
  }
};

// API to fetch SOR prompts
const fetchPrompts = async (
  params?: PromptsParams,
  options?: { signal?: AbortSignal }
): Promise<PromptResponse> => {
  try {
    const queryParams = new URLSearchParams(
      Object.entries(params)
        .filter(([, v]) => v !== undefined)
        .map(([k, v]) => [k, String(v)])
    ).toString();

    const response = await fetch(
      `${BASE_URL}/api/v1/sor/aeo/prompt?${queryParams}`,
      { ...getPayload(constructHeaders(), 'GET'), signal: options?.signal }
    );

    if (!response.ok) {
      throw new Error('Failed to fetch prompts');
    }

    return response.json();
  } catch (error) {
    if ((error as Error)?.name === 'AbortError') throw error;
    logger(error as Error, { context: 'fetchPrompts: Error fetching prompts' });
    throw new Error('Error fetching prompts');
  }
};

// API to fetch a single SOR prompt detail (per-model samples) within a window.
const fetchPromptDetail = async (
  uuid: string,
  from: string,
  to: string,
  countryCode?: string | null,
): Promise<PromptDetailResponse> => {
  try {
    const params: Record<string, string> = { from, to };
    if (countryCode) params.country_code = countryCode;
    const queryParams = new URLSearchParams(params).toString();
    const response = await fetch(
      `${BASE_URL}/api/v1/sor/aeo/prompt/${encodeURIComponent(uuid)}?${queryParams}`,
      getPayload(constructHeaders(), 'GET'),
    );

    if (!response.ok) {
      throw new Error('Failed to fetch prompt detail');
    }

    return response.json();
  } catch (error) {
    logger(error as Error, { context: 'fetchPromptDetail: Error fetching prompt detail' });
    throw new Error('Error fetching prompt detail');
  }
};

// API to fetch Blog Post Refresh Tasks
const fetchBlogPostRefreshTasks = async (key: string): Promise<BlogPostRefreshTaskResponse> => {
  try {
    const response = await fetch(
      `${BASE_URL}/api/v1/sor/blog_post/${encodeURIComponent(key)}/blog_post_refresh_task`,
      getPayload(constructHeaders(), 'GET')
    );

    if (!response.ok) {
      throw new Error('Failed to fetch blog post refresh tasks');
    }

    return response.json();
  } catch (error) {
    logger(error as Error, { context: 'fetchBlogPostRefreshTasks: Error fetching blog post refresh tasks' });
    throw new Error('Error fetching blog post refresh tasks');
  }
};

// API to fetch Blog Post Outline Tasks
const fetchBlogPostOutlineTasks = async (key: string): Promise<BlogPostOutlineTaskResponse> => {
  try {
    const response = await fetch(
      `${BASE_URL}/api/v1/sor/blog_post/${encodeURIComponent(key)}/blog_post_outline_task`,
      getPayload(constructHeaders(), 'GET')
    );

    if (!response.ok) {
      throw new Error('Failed to fetch blog post outline tasks');
    }

    return response.json();
  } catch (error) {
    logger(error as Error, { context: 'fetchBlogPostOutlineTasks: Error fetching blog post outline tasks' });
    throw new Error('Error fetching blog post outline tasks');
  }
};

// API to update a Blog Post Outline Task
const updateBlogPostOutlineTask = async (
  key: string,
  payload: {
    key: string;
    user_modified_content?: BlogSectionCollection;
    status?: BlogPostOutlineTask['status'];
    metadata?: Record<string, unknown>;
  }
): Promise<{ blog_post_outline_task: BlogPostOutlineTask }> => {
  try {
    const response = await fetch(
      `${BASE_URL}/api/v1/sor/blog_post/${encodeURIComponent(key)}/blog_post_outline_task`,
      getPayload(constructHeaders(), 'PATCH', { blog_post_outline_task: payload })
    );

    if (!response.ok) {
      throw new Error('Failed to update blog post outline task');
    }

    return response.json();
  } catch (error) {
    logger(error as Error, { context: 'updateBlogPostOutlineTask: Error updating blog post outline task' });
    throw new Error('Error updating blog post outline task');
  }
};

// API to update a Blog Post Refresh Task
const updateBlogPostRefreshTask = async (
  key: string,
  payload: {
    key: string;
    user_modified_content?: BlogSectionCollection;
    status?: BlogPostRefreshTask['status'];
    metadata?: Record<string, unknown>;
  }
): Promise<{ blog_post_refresh_task: BlogPostRefreshTask }> => {
  try {
    const response = await fetch(
      `${BASE_URL}/api/v1/sor/blog_post/${encodeURIComponent(key)}/blog_post_refresh_task`,
      getPayload(constructHeaders(), 'PATCH', { blog_post_refresh_task: payload })
    );

    if (!response.ok) {
      throw new Error('Failed to update blog post refresh task');
    }

    return response.json();
  } catch (error) {
    logger(error as Error, { context: 'updateBlogPostRefreshTask: Error updating blog post refresh task' });
    throw new Error('Error updating blog post refresh task');
  }
};

// API to create a new insight
const createInsight = async (payload: InsightCreatePayload): Promise<Insight> => {
  try {
    const response = await fetch(
      `${BASE_URL}/api/v1/insight`,
      getPayload(constructHeaders(), 'POST', payload)
    );

    if (response.ok) {
      const data = await response.json();
      return data.insight;
    }
    throw new Error('Error creating insight');
  } catch (error) {
    logger(error as Error, { context: 'createInsight: Error creating insight' });
    throw error;
  }
};

/**
 * Create an HTML insight from pre-rendered content. The server stores it in
 * `READY` state with `type=HTML` and does not trigger the insight-generation
 * workflow. Used by the Report Studio bootstrap path — a minimal HTML
 * scaffold is created up-front so the studio has a real insight to sync to
 * the sandbox and stream into the canvas; the chat agent then fills it in
 * via `update_file`.
 */
const createHtmlInsight = async (payload: HtmlInsightCreatePayload): Promise<Insight> => {
  const response = await fetch(
    `${BASE_URL}/api/v1/insight`,
    getPayload(constructHeaders(), 'POST', payload)
  );

  if (!response.ok) {
    const message = `Failed to create HTML insight (HTTP ${response.status})`;
    logger(new Error(message), { context: 'createHtmlInsight' });
    throw new Error(message);
  }

  const data = await response.json();
  return data.insight;
};

/**
 * Rename a custom (HTML) report. `PATCH /insight/:id` with the new
 * `display_name`. The server rejects non-HTML insights, so callers should only
 * offer this for custom reports. Returns the updated insight.
 */
const renameInsight = async (
  insightId: string,
  displayName: string,
): Promise<Insight> => {
  try {
    const response = await fetch(
      `${BASE_URL}/api/v1/insight/${encodeURIComponent(insightId)}`,
      getPayload(constructHeaders(), 'PATCH', { insight: { display_name: displayName } })
    );

    if (response.ok) {
      const data = await response.json();
      return data.insight;
    }
    throwApiError('Error renaming report', response.status);
  } catch (error) {
    if (error instanceof ApiError) throw error;
    logger(error as Error, { context: 'renameInsight: Error renaming report' });
    throw new Error('Error renaming report');
  }
};

/**
 * Delete a custom (HTML) report. `DELETE /insight/:id`. The server rejects
 * non-HTML insights and cleans up the report's history + sandbox copy.
 */
const deleteInsight = async (insightId: string): Promise<void> => {
  try {
    const response = await fetch(
      `${BASE_URL}/api/v1/insight/${encodeURIComponent(insightId)}`,
      getPayload(constructHeaders(), 'DELETE')
    );
    if (response.ok || response.status === 204) return;
    throwApiError('Error deleting report', response.status);
  } catch (error) {
    if (error instanceof ApiError) throw error;
    logger(error as Error, { context: 'deleteInsight: Error deleting report' });
    throw new Error('Error deleting report');
  }
};

/**
 * Fetch the raw HTML content of an insight as text. Backend serves the
 * content with `Content-Type: text/html`.
 *
 * Used by the Report Studio to populate the code-view tab (the iframe
 * preview can stream directly from `insight.html_url` so we don't need
 * to round-trip the body for it).
 */
const fetchInsightHtml = async (insightId: string): Promise<string> => {
  const response = await fetch(
    `${BASE_URL}/api/v1/insight/${insightId}/html`,
    getPayload(constructHeaders(), 'GET')
  );

  if (!response.ok) {
    const message = `Failed to fetch insight HTML for ${insightId} (HTTP ${response.status})`;
    logger(new Error(message), { context: 'fetchInsightHtml' });
    throw new Error(message);
  }

  return response.text();
};

/**
 * Upload a single file to the tenant file store via `POST /file` (multipart).
 * Returns the persisted file plus a freshly presigned GET URL.
 *
 * Deliberately bypasses `constructHeaders`/`getPayload`: a multipart body must
 * NOT be JSON-stringified, and the browser must set its own
 * `multipart/form-data` boundary, so we send raw FormData with no
 * `Content-Type` header.
 */
interface FileUploadResponse {
  file: ChatAttachment;
}

const uploadFile = async (file: File): Promise<ChatAttachment> => {
  try {
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${BASE_URL}/api/v1/file`, {
      method: 'POST',
      body: formData,
    });

    if (response.ok) {
      const data = (await response.json()) as FileUploadResponse;
      return data.file;
    }
    const errorBody = await response.json().catch(() => ({}));
    throw new ApiError(
      errorBody?.message || 'Error uploading file',
      response.status,
      errorBody?.code
    );
  } catch (error) {
    logger(error as Error, { context: 'uploadFile: Error uploading file' });
    throw error;
  }
};

// API to fetch SOR topics
const fetchTopics = async () => {
  try {
    const response = await fetch(
      `${BASE_URL}/api/v1/sor/aeo/topic`,
      getPayload(constructHeaders(), 'GET')
    );

    if (!response.ok) {
      throw new Error('Failed to fetch topics');
    }

    const data = await response.json();
    return data.topics ?? [];
  } catch (error) {
    logger(error as Error, { context: 'fetchTopics: Error fetching topics' });
    throw new Error('Error fetching topics');
  }
};

// API to create a SOR topic
const createTopic = async (body: { topic: string; description?: string }) => {
  try {
    const response = await fetch(
      `${BASE_URL}/api/v1/sor/aeo/topic`,
      getPayload(constructHeaders(), 'POST', body)
    );

    if (!response.ok) {
      throw new Error('Failed to create topic');
    }

    const data = await response.json();
    return data.topic;
  } catch (error) {
    logger(error as Error, { context: 'createTopic: Error creating topic' });
    throw new Error('Error creating topic');
  }
};

export type InviteRole = 'admin' | 'member' | 'guest';

// API to invite a member to the current organization. Routes through the
// backend (not Clerk's frontend SDK) so guest invites can be tagged via the
// org-invitation public metadata that becomes the membership's `strive_roles` array.
const inviteOrganizationMember = async (body: { email_address: string; role: InviteRole }) => {
  const response = await fetch(
    `${BASE_URL}/api/v1/tenant/invitation`,
    getPayload(constructHeaders(), 'POST', body),
  );

  if (!response.ok) {
    // Surface the backend's specific reason (e.g. "already invited") when present.
    let message = 'Failed to send invitation';
    try {
      const errorBody = await response.json();
      message = errorBody?.message || errorBody?.error || message;
    } catch {
      // Response had no JSON body — keep the generic message.
    }
    throw new Error(message);
  }

  const data = await response.json();
  return data.invitation;
};

// API to create a managed AEO prompt
export class CreatePromptError extends Error {
  public readonly status: number;

  public readonly isDuplicate: boolean;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'CreatePromptError';
    this.status = status;
    this.isDuplicate = status === 409;
  }
}

const createPromptManaged = async (body: {
  uuid: string;
  prompt: string;
  sor_topic_id: string;
  intent?: string;
  cron: string;
  cron_timezone: string;
}) => {
  const response = await fetch(
    `${BASE_URL}/api/v1/sor/aeo/prompt/managed`,
    getPayload(constructHeaders(), 'POST', body),
  );

  if (!response.ok) {
    // Try to surface the backend error message (e.g. duplicate prompt) instead
    // of a generic failure. Falls back to a status-based message if parsing fails.
    let message = response.status === 409
      ? 'You already have this prompt'
      : 'Failed to create prompt';
    try {
      const errBody = await response.json();
      if (errBody?.message) message = errBody.message;
      else if (errBody?.error) message = errBody.error;
    } catch {
      // body was not JSON — keep the default message.
    }
    const err = new CreatePromptError(message, response.status);
    logger(err, { context: 'createPromptManaged: Error creating prompt' });
    throw err;
  }

  const data = await response.json();
  return data.prompt;
};

// API to soft-delete an AEO prompt (sets is_active = false).
// Hides the prompt from the UI and stops the workflow from picking it for
// future polling cycles. Historical samples are preserved in the DB.
const deletePromptManaged = async (id: string) => {
  try {
    const response = await fetch(
      `${BASE_URL}/api/v1/sor/aeo/prompt/managed/${id}`,
      getPayload(constructHeaders(), 'DELETE')
    );

    if (!response.ok) {
      throw new Error('Failed to delete prompt');
    }

    return response.json();
  } catch (error) {
    logger(error as Error, { context: 'deletePromptManaged: Error deleting prompt' });
    throw new Error('Error deleting prompt');
  }
};

// API to fetch managed AEO prompts (from sor_aeo_prompt table)
const fetchManagedPrompts = async () => {
  try {
    const response = await fetch(
      `${BASE_URL}/api/v1/sor/aeo/prompt/managed`,
      getPayload(constructHeaders(), 'GET')
    );

    if (!response.ok) {
      throw new Error('Failed to fetch managed prompts');
    }

    const data = await response.json();
    return data.prompts ?? [];
  } catch (error) {
    logger(error as Error, { context: 'fetchManagedPrompts: Error' });
    throw new Error('Error fetching managed prompts');
  }
};

// AEO Excel export — POST receives an xlsx binary stream
export interface AeoExcelExportPayload {
  from: string;
  to: string;
  country_code?: string;
  model?: string;
  location_label?: string;
  brand_name?: string;
}

const FALLBACK_AEO_FILENAME = 'aeo-export.xlsx';

const parseContentDispositionFilename = (header: string | null): string | null => {
  if (!header) return null;
  const match = /filename="?([^"]+)"?/i.exec(header);
  return match ? match[1] : null;
};

const downloadAeoExcel = async (
  payload: AeoExcelExportPayload,
  options?: { signal?: AbortSignal }
): Promise<{ blob: Blob; filename: string }> => {
  try {
    const response = await fetch(
      `${BASE_URL}/api/v1/sor/aeo/export`,
      { ...getPayload(constructHeaders(), 'POST', payload), signal: options?.signal }
    );

    if (!response.ok) {
      throw new Error('Failed to download AEO export');
    }

    const blob = await response.blob();
    const filename = parseContentDispositionFilename(
      response.headers.get('content-disposition')
    ) ?? FALLBACK_AEO_FILENAME;
    return { blob, filename };
  } catch (error) {
    if ((error as Error)?.name === 'AbortError') throw error;
    logger(error as Error, { context: 'downloadAeoExcel: Error downloading export' });
    throw new Error('Error downloading AEO export');
  }
};

// API to fetch prompt coverage by topic
const fetchPromptCoverage = async (
  params?: { from?: string; to?: string; country_code?: string; model?: string },
) => {
  try {
    const queryParams = params
      ? new URLSearchParams(
          Object.entries(params)
            .filter(([, v]) => v !== undefined)
            .map(([k, v]) => [k, String(v)])
        ).toString()
      : '';
    const url = queryParams
      ? `${BASE_URL}/api/v1/sor/aeo/prompt/coverage?${queryParams}`
      : `${BASE_URL}/api/v1/sor/aeo/prompt/coverage`;
    const response = await fetch(url, getPayload(constructHeaders(), 'GET'));

    if (!response.ok) {
      throw new Error('Failed to fetch prompt coverage');
    }

    return response.json();
  } catch (error) {
    logger(error as Error, { context: 'fetchPromptCoverage: Error' });
    throw new Error('Error fetching prompt coverage');
  }
};

const fetchPromptUUIDs = async (countryCode?: string | null): Promise<string[]> => {
  try {
    const query = countryCode
      ? `?${new URLSearchParams({ country_code: countryCode }).toString()}`
      : '';
    const response = await fetch(
      `${BASE_URL}/api/v1/sor/aeo/prompt/ids${query}`,
      getPayload(constructHeaders(), 'GET')
    );

    if (!response.ok) {
      throwApiError('Failed to fetch prompt UUIDs', response.status);
    }

    const data = await response.json();
    return data.prompt_uuids ?? [];
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    logger(error as Error, { context: 'fetchPromptUUIDs: Error fetching prompt UUIDs' });
    throw new Error('Error fetching prompt UUIDs');
  }
};

export interface ChatMode {
  id: string;
  label: string;
  description: string;
  is_default: boolean;
}

// API to list chat modes (platform-wide catalog; model name never exposed)
const listChatModes = async (): Promise<{ chat_modes: ChatMode[] }> => {
  try {
    const response = await fetch(
      `${BASE_URL}/api/v1/chat/modes`,
      getPayload(constructHeaders())
    );
    if (response.ok) return await response.json();
    throwApiError('Error fetching chat modes', response.status);
  } catch (error) {
    if (error instanceof ApiError) throw error;
    logger(error as Error, { context: 'listChatModes: Error fetching chat modes' });
    throw new Error('Error fetching chat modes');
  }
};

// API to list cards
const listCards = async (params?: CardQueryParams) => {
  try {
    const queryParams = params
      ? `?${new URLSearchParams(
          Object.fromEntries(
            Object.entries(params)
              .filter(([, v]) => v !== undefined)
              .map(([k, v]) => [k, String(v)])
          )
        ).toString()}`
      : '';
    const response = await fetch(
      `${BASE_URL}/api/v1/card${queryParams}`,
      getPayload(constructHeaders())
    );
    if (response.ok) return await response.json();
    throwApiError('Error fetching cards', response.status);
  } catch (error) {
    if (error instanceof ApiError) throw error;
    logger(error as Error, { context: 'listCards: Error fetching cards' });
    throw new Error('Error fetching cards');
  }
};

// API to fetch a single card by id (tenant-scoped server-side; cross-tenant → 404)
const getCardById = async (cardId: string) => {
  try {
    const response = await fetch(
      `${BASE_URL}/api/v1/card/${encodeURIComponent(cardId)}`,
      getPayload(constructHeaders())
    );
    if (response.ok) return await response.json();
    throwApiError('Error fetching card', response.status);
  } catch (error) {
    if (error instanceof ApiError) throw error;
    logger(error as Error, { context: 'getCardById: Error fetching card' });
    throw new Error('Error fetching card');
  }
};

// API to update card status
const updateCardStatus = async (cardId: number, payload: CardUpdatePayload) => {
  try {
    const response = await fetch(
      `${BASE_URL}/api/v1/card/${cardId}`,
      getPayload(constructHeaders(), 'PATCH', payload)
    );
    if (response.ok) return await response.json();
    throwApiError('Error updating card', response.status);
  } catch (error) {
    if (error instanceof ApiError) throw error;
    logger(error as Error, { context: 'updateCardStatus: Error updating card' });
    throw new Error('Error updating card');
  }
};

// API to list experiments
const listExperiments = async ({ page = 1, page_size = 20 }: { page?: number; page_size?: number } = {}) => {
  try {
    const params = new URLSearchParams({ page: String(page), page_size: String(page_size) });
    const response = await fetch(
      `${BASE_URL}/api/v1/sor/experiment?${params}`,
      getPayload(constructHeaders())
    );
    if (response.ok) return await response.json();
    throwApiError('Error fetching experiments', response.status);
  } catch (error) {
    if (error instanceof ApiError) throw error;
    logger(error as Error, { context: 'listExperiments: Error fetching experiments' });
    throw new Error('Error fetching experiments');
  }
};

// API to list skills
const listSkills = async () => {
  try {
    const response = await fetch(
      `${BASE_URL}/api/v1/skill`,
      getPayload(constructHeaders())
    );
    if (response.ok) return await response.json();
    throwApiError('Error fetching skills', response.status);
  } catch (error) {
    if (error instanceof ApiError) throw error;
    logger(error as Error, { context: 'listSkills: Error fetching skills' });
    throw new Error('Error fetching skills');
  }
};

// ============================================================================
// Tenant Skills
//
// A tenant's own skills (installed from the repository, authored inline, or
// strive-provisioned system skills). `listSkills` above lists the repository
// catalog to install from; these CRUD the tenant's instances.
// ============================================================================

const listTenantSkills = async (): Promise<TenantSkillsResponse> => {
  try {
    const response = await fetch(
      `${BASE_URL}/api/v1/tenant_skill`,
      getPayload(constructHeaders())
    );
    if (response.ok) return await response.json();
    throwApiError('Error fetching tenant skills', response.status);
  } catch (error) {
    if (error instanceof ApiError) throw error;
    logger(error as Error, { context: 'listTenantSkills' });
    throw new Error('Error fetching tenant skills');
  }
};

const createTenantSkill = async (
  payload: TenantSkillCreateParams
): Promise<TenantSkillResponse> => {
  try {
    const response = await fetch(
      `${BASE_URL}/api/v1/tenant_skill`,
      getPayload(constructHeaders(), 'POST', { tenant_skill: payload })
    );
    if (response.ok) return await response.json();
    await throwApiErrorWithBody(response, 'Error creating skill');
  } catch (error) {
    if (error instanceof ApiError) throw error;
    logger(error as Error, { context: 'createTenantSkill' });
    throw new Error('Error creating skill');
  }
};

const updateTenantSkill = async (
  id: string,
  payload: TenantSkillUpdateParams
): Promise<TenantSkillResponse> => {
  try {
    const response = await fetch(
      `${BASE_URL}/api/v1/tenant_skill/${encodeURIComponent(id)}`,
      getPayload(constructHeaders(), 'PATCH', { tenant_skill: payload })
    );
    if (response.ok) return await response.json();
    await throwApiErrorWithBody(response, 'Error updating skill');
  } catch (error) {
    if (error instanceof ApiError) throw error;
    logger(error as Error, { context: 'updateTenantSkill' });
    throw new Error('Error updating skill');
  }
};

const deleteTenantSkill = async (id: string): Promise<void> => {
  try {
    const response = await fetch(
      `${BASE_URL}/api/v1/tenant_skill/${encodeURIComponent(id)}`,
      getPayload(constructHeaders(), 'DELETE')
    );
    if (response.ok || response.status === 204) return;
    throwApiError('Error deleting skill', response.status);
  } catch (error) {
    if (error instanceof ApiError) throw error;
    logger(error as Error, { context: 'deleteTenantSkill' });
    throw new Error('Error deleting skill');
  }
};

/** Response shape from the create dataset endpoint. */
interface CreateDatasetResponse {
  dataset: {
    id: string;
    display_name: string;
    ready_for_use: boolean;
    sync_status: string;
  };
}

/**
 * Creates a dataset for a credential.
 *
 * @param credentialId - The credential ID.
 * @param displayName - The display name for the dataset.
 * @returns The created dataset response.
 */
const createDataset = async (
  credentialId: string,
  displayName: string
): Promise<CreateDatasetResponse> => {
  try {
    const body = {
      dataset: { display_name: displayName }
    };
    const response = await fetch(
      `${BASE_URL}/api/v1/credential/${credentialId}/dataset`,
      getPayload(constructHeaders(), "POST", body)
    );

    if (response.ok) {
      return await response.json();
    }
    const errorBody = await response.json().catch(() => ({}));
    throw new ApiError(
      errorBody?.message || 'Error creating dataset',
      response.status,
      errorBody?.code
    );
  } catch (error) {
    logger(
      error as Error,
      { context: 'createDataset: Error creating dataset' }
    );
    throw error;
  }
};

/**
 * Deletes a dataset for a credential.
 *
 * @param credentialId - The credential ID.
 * @param datasetId - The dataset ID.
 */
const deleteDataset = async (
  credentialId: string,
  datasetId: string
): Promise<void> => {
  try {
    const response = await fetch(
      `${BASE_URL}/api/v1/credential/${credentialId}/dataset/${datasetId}`,
      getPayload(constructHeaders(), "DELETE")
    );

    if (response.ok || response.status === 204) {
      return;
    }
    throw new Error('Error deleting dataset');
  } catch (error) {
    logger(
      error as Error,
      { context: 'deleteDataset: Error deleting dataset' }
    );
    throw new Error('Error deleting dataset', { cause: error });
  }
};

// API to list routines
const listRoutines = async () => {
  try {
    const response = await fetch(
      `${BASE_URL}/api/v1/routine`,
      getPayload(constructHeaders())
    );
    if (response.ok) return await response.json();
    throwApiError('Error fetching routines', response.status);
  } catch (error) {
    if (error instanceof ApiError) throw error;
    logger(error as Error, { context: 'listRoutines: Error fetching routines' });
    throw new Error('Error fetching routines');
  }
};

// API to fetch a single routine by id
const getRoutine = async (routineId: string) => {
  try {
    const response = await fetch(
      `${BASE_URL}/api/v1/routine/${encodeURIComponent(routineId)}`,
      getPayload(constructHeaders())
    );
    if (response.ok) return await response.json();
    throwApiError('Error fetching routine', response.status);
  } catch (error) {
    if (error instanceof ApiError) throw error;
    logger(error as Error, { context: 'getRoutine: Error fetching routine' });
    throw new Error('Error fetching routine');
  }
};

// API to update a routine. Accepts a partial patch (schedule and/or
// output_channels); only the provided keys are forwarded, so schedule-only and
// channels-only callers both work without sending a stale sibling field.
const updateRoutine = async (
  routineId: string,
  patch: Record<string, unknown>
) => {
  try {
    const response = await fetch(
      `${BASE_URL}/api/v1/routine/${encodeURIComponent(routineId)}`,
      getPayload(constructHeaders(), 'PATCH', { routine: patch })
    );
    if (response.ok) return await response.json();
    throwApiError('Error updating routine', response.status);
  } catch (error) {
    if (error instanceof ApiError) throw error;
    logger(error as Error, { context: 'updateRoutine: Error updating routine' });
    throw new Error('Error updating routine');
  }
};

// API to delete a workflow execution (chat history)
const deleteWorkflowExecution = async (
  workflowInstanceId: string,
  workflowExecutionId: string
): Promise<void> => {
  try {
    const response = await fetch(
      `${BASE_URL}/api/v1/workflow_instance/${encodeURIComponent(workflowInstanceId)}/workflow_execution/${encodeURIComponent(workflowExecutionId)}`,
      getPayload(constructHeaders(), 'DELETE')
    );
    if (response.ok || response.status === 204) return;
    throwApiError('Error deleting chat', response.status);
  } catch (error) {
    if (error instanceof ApiError) throw error;
    logger(error as Error, { context: 'deleteWorkflowExecution: Error deleting chat' });
    throw new Error('Error deleting chat');
  }
};

/**
 * Update a workflow execution's title. `PATCH /workflow_instance/:iid/workflow_execution/:eid`
 * with `{ workflow_execution: { title } }`. The server trims the value and
 * collapses empty strings to `null`, so callers can pass either form to clear
 * the title. Returns the updated execution.
 */
const updateWorkflowExecution = async (
  workflowInstanceId: string,
  workflowExecutionId: string,
  title: string | null,
) => {
  try {
    const response = await fetch(
      `${BASE_URL}/api/v1/workflow_instance/${encodeURIComponent(workflowInstanceId)}/workflow_execution/${encodeURIComponent(workflowExecutionId)}`,
      getPayload(constructHeaders(), 'PATCH', { workflow_execution: { title } })
    );
    if (response.ok) {
      const data = await response.json();
      return data.workflow_execution;
    }
    throwApiError('Error updating chat', response.status);
  } catch (error) {
    if (error instanceof ApiError) throw error;
    logger(error as Error, { context: 'updateWorkflowExecution: Error updating chat' });
    throw new Error('Error updating chat');
  }
};

/**
 * Set the active chat mode on a workflow execution. Mirrors
 * updateWorkflowExecution's PATCH shape but targets chat_mode_id instead of
 * title.
 */
const setChatMode = async (
  workflowInstanceId: string,
  workflowExecutionId: string,
  modeId: string,
) => {
  try {
    const response = await fetch(
      `${BASE_URL}/api/v1/workflow_instance/${encodeURIComponent(workflowInstanceId)}/workflow_execution/${encodeURIComponent(workflowExecutionId)}`,
      getPayload(constructHeaders(), 'PATCH', { workflow_execution: { chat_mode_id: modeId } })
    );
    if (response.ok) {
      const data = await response.json();
      return data.workflow_execution;
    }
    throwApiError('Error setting chat mode', response.status);
  } catch (error) {
    if (error instanceof ApiError) throw error;
    logger(error as Error, { context: 'setChatMode: Error setting chat mode' });
    throw new Error('Error setting chat mode');
  }
};

// API to delete a routine
const deleteRoutine = async (routineId: string): Promise<void> => {
  try {
    const response = await fetch(
      `${BASE_URL}/api/v1/routine/${encodeURIComponent(routineId)}`,
      getPayload(constructHeaders(), 'DELETE')
    );
    if (response.ok || response.status === 204) return;
    throwApiError('Error deleting routine', response.status);
  } catch (error) {
    if (error instanceof ApiError) throw error;
    logger(error as Error, { context: 'deleteRoutine: Error deleting routine' });
    throw new Error('Error deleting routine');
  }
};

// API to run a routine on demand. The server enqueues the run and responds 202;
// the routine's schedule is unaffected. The empty-object body is required:
// headers carry Content-Type: application/json, and Fastify rejects a body-less
// JSON POST (FST_ERR_CTP_EMPTY_JSON_BODY) before it reaches the route.
const triggerRoutine = async (routineId: string): Promise<void> => {
  try {
    const response = await fetch(
      `${BASE_URL}/api/v1/routine/${encodeURIComponent(routineId)}/trigger`,
      getPayload(constructHeaders(), 'POST', {})
    );
    if (response.ok) return;
    throwApiError('Error running routine', response.status);
  } catch (error) {
    if (error instanceof ApiError) throw error;
    logger(error as Error, { context: 'triggerRoutine: Error running routine' });
    throw new Error('Error running routine');
  }
};

// ============================================================================
// Assignment
// Mirrors apps/server/src/assignment/router.ts
// ============================================================================

interface ListAssignmentsParams {
  category?: AssignmentCategoryValue;
  agent_short_name?: string;
}

const listAssignments = async (
  params?: ListAssignmentsParams,
): Promise<AssignmentListResponse> => {
  try {
    const search = new URLSearchParams();
    if (params?.category) search.set('category', params.category);
    if (params?.agent_short_name) search.set('agent_short_name', params.agent_short_name);
    const queryString = search.toString();
    const url = queryString
      ? `${BASE_URL}/api/v1/assignment?${queryString}`
      : `${BASE_URL}/api/v1/assignment`;
    const response = await fetch(url, getPayload(constructHeaders()));
    if (response.ok) return await response.json();
    throwApiError('Error fetching assignments', response.status);
  } catch (error) {
    if (error instanceof ApiError) throw error;
    logger(error as Error, { context: 'listAssignments' });
    throw new Error('Error fetching assignments');
  }
  throw new Error('Error fetching assignments');
};

const getAssignment = async (id: string): Promise<AssignmentResponse> => {
  try {
    const response = await fetch(
      `${BASE_URL}/api/v1/assignment/${encodeURIComponent(id)}`,
      getPayload(constructHeaders()),
    );
    if (response.ok) return await response.json();
    throwApiError('Error fetching assignment', response.status);
  } catch (error) {
    if (error instanceof ApiError) throw error;
    logger(error as Error, { context: 'getAssignment' });
    throw new Error('Error fetching assignment');
  }
  throw new Error('Error fetching assignment');
};

interface CreateAssignmentPayload {
  agent_short_name: string;
  workflow_short_name?: string;
  title: string;
  context: string;
  category?: AssignmentCategoryValue;
  resources?: { resource_type: string; resource_id: string }[];
}

const createAssignment = async (
  payload: CreateAssignmentPayload,
): Promise<AssignmentResponse> => {
  try {
    const response = await fetch(
      `${BASE_URL}/api/v1/assignment`,
      getPayload(constructHeaders(), 'POST', { assignment: payload }),
    );
    if (response.ok) return await response.json();
    throwApiError('Error creating assignment', response.status);
  } catch (error) {
    if (error instanceof ApiError) throw error;
    logger(error as Error, { context: 'createAssignment' });
    throw new Error('Error creating assignment');
  }
  throw new Error('Error creating assignment');
};

// ============================================================================
// Knowledge Hub
// See apps/client/platform/docs/designs/unified-marketing-database/knowledge_hub.api.html
// ============================================================================

const listKnowledgeHubs = async (
  options: { parseFrontmatter?: boolean } = {}
): Promise<KnowledgeHubListResponse> => {
  try {
    const params = new URLSearchParams();
    if (options.parseFrontmatter) params.set('parse_frontmatter', 'true');
    const qs = params.toString();
    const response = await fetch(
      `${BASE_URL}/api/v1/knowledge_hub${qs ? `?${qs}` : ''}`,
      getPayload(constructHeaders())
    );
    if (response.ok) return await response.json();
    throwApiError('Error fetching knowledge hub files', response.status);
  } catch (error) {
    if (error instanceof ApiError) throw error;
    logger(error as Error, { context: 'listKnowledgeHubs' });
    throw new Error('Error fetching knowledge hub files');
  }
  throw new Error('Error fetching knowledge hub files');
};

const getKnowledgeHub = async (id: string): Promise<KnowledgeHubResponse> => {
  try {
    const response = await fetch(
      `${BASE_URL}/api/v1/knowledge_hub/${encodeURIComponent(id)}`,
      getPayload(constructHeaders())
    );
    if (response.ok) return await response.json();
    throwApiError('Error fetching knowledge hub file', response.status);
  } catch (error) {
    if (error instanceof ApiError) throw error;
    logger(error as Error, { context: 'getKnowledgeHub' });
    throw new Error('Error fetching knowledge hub file');
  }
  throw new Error('Error fetching knowledge hub file');
};

const createKnowledgeHub = async (
  payload: KnowledgeHubCreateParams
): Promise<KnowledgeHubResponse> => {
  try {
    const response = await fetch(
      `${BASE_URL}/api/v1/knowledge_hub`,
      getPayload(constructHeaders(), 'POST', { knowledge_hub: payload })
    );
    if (response.ok) return await response.json();
    // Surface the server error message so the UI can show the precise validation/conflict reason.
    const text = await response.text();
    let message = 'Error creating knowledge hub file';
    try {
      const parsed = JSON.parse(text);
      if (parsed?.message) message = parsed.message;
    } catch { /* fall through with default */ }
    throwApiError(message, response.status);
  } catch (error) {
    if (error instanceof ApiError) throw error;
    logger(error as Error, { context: 'createKnowledgeHub' });
    throw new Error('Error creating knowledge hub file');
  }
  throw new Error('Error creating knowledge hub file');
};

const updateKnowledgeHub = async (
  id: string,
  payload: KnowledgeHubUpdateParams
): Promise<KnowledgeHubResponse> => {
  try {
    const response = await fetch(
      `${BASE_URL}/api/v1/knowledge_hub/${encodeURIComponent(id)}`,
      getPayload(constructHeaders(), 'PATCH', { knowledge_hub: payload })
    );
    if (response.ok) return await response.json();
    const text = await response.text();
    let message = 'Error updating knowledge hub file';
    try {
      const parsed = JSON.parse(text);
      if (parsed?.message) message = parsed.message;
    } catch { /* fall through */ }
    throwApiError(message, response.status);
  } catch (error) {
    if (error instanceof ApiError) throw error;
    logger(error as Error, { context: 'updateKnowledgeHub' });
    throw new Error('Error updating knowledge hub file');
  }
  throw new Error('Error updating knowledge hub file');
};

const deleteKnowledgeHub = async (id: string): Promise<void> => {
  try {
    const response = await fetch(
      `${BASE_URL}/api/v1/knowledge_hub/${encodeURIComponent(id)}`,
      getPayload(constructHeaders(), 'DELETE')
    );
    if (response.ok || response.status === 204) return;
    throwApiError('Error deleting knowledge hub file', response.status);
  } catch (error) {
    if (error instanceof ApiError) throw error;
    logger(error as Error, { context: 'deleteKnowledgeHub' });
    throw new Error('Error deleting knowledge hub file');
  }
};

type SeoAuditFilterOp = 'gt' | 'lt' | 'gte' | 'lte' | 'eq' | 'ne' | 'in' | 'substring';
type SeoAuditFilterValue = string | number | boolean | (string | number)[];

interface SeoAuditTableFilter {
  column: string;
  op: SeoAuditFilterOp;
  value: SeoAuditFilterValue;
}

interface SeoAuditTableQuery {
  filters?: SeoAuditTableFilter[];
  search?: string;
  compareWindowDays?: number;
}

interface SeoAuditTablePageInput {
  table: string;
  brand_domain: string;
  country: string;
  date: string;
  date_compared?: string;
  competitors?: string[];
  offset: number;
  limit: number;
  query?: SeoAuditTableQuery;
}

interface SeoAuditTablePageCredential {
  id: string;
  name?: string;
}

interface SeoAuditTablePageResponse<TRow> {
  rows: TRow[];
  hasMore: boolean | 'unknown';
}

const AHREFS_APP_NAME = 'ahrefs';
const FETCH_SEO_AUDIT_TABLE_PAGE_ACTION = 'fetch_seo_audit_table_page';

const fetchSeoAuditTablePage = async <TRow>(
  input: SeoAuditTablePageInput,
  credentials: SeoAuditTablePageCredential[]
): Promise<SeoAuditTablePageResponse<TRow>> => {
  const result = await executeAppAction(
    BASE_URL,
    AHREFS_APP_NAME,
    FETCH_SEO_AUDIT_TABLE_PAGE_ACTION,
    credentials,
    [ input ]
  );
  if (result?.status !== 'success') {
    const message = typeof result?.error?.message === 'string'
      ? result.error.message
      : 'Error fetching SEO audit table page';
    throw new Error(message);
  }
  const data = result?.items?.[0]?.data as SeoAuditTablePageResponse<TRow> | undefined;
  return {
    rows: data?.rows ?? [],
    hasMore: data?.hasMore === 'unknown' ? 'unknown' : Boolean(data?.hasMore)
  };
};

export type {
  TriggerWorkflowPayload, CreateAgentInstancePayload, UpdateAgentInstancePayload, KeyValPayload,
  App, AppAuth, Insight, InsightReport, InsightReportData, Block, BlockElement, BlockConfig,
  Processor, InsightDefinition, InsightCreatePayload, HtmlInsightCreatePayload, CardQueryParams, CardUpdatePayload,
  SeoAuditTablePageInput, SeoAuditTablePageCredential, SeoAuditTablePageResponse,
  SeoAuditFilterOp, SeoAuditFilterValue, SeoAuditTableFilter, SeoAuditTableQuery
};

export {
  BASE_URL,
  inviteOrganizationMember,
  listAgents,
  listAgentInstances,
  createNewCredential,
  patchCredential,
  patchCredentialStatus,
  deleteCredential,
  fetchAllCredential,
  getTenantDetails,
  updateTenantSettings,
  updateTenantOnboarded,
  createAgentInstance,
  installAgent,
  updateAgentInstance,
  triggerWorkflow,
  triggerChatWorkflow,
  getWorkflowExecutionResources,
  attachWorkflowExecutionResources,
  chatWithWorkflowStream,
  executePendingTools,
  getWorkFlowInstanceExecutionStatus,
  listAllWorkflowInstances,
  getWorkflowExecutionState,
  listAllApprovals,
  updateApproval,
  listAllApprovalItems,
  updateApprovalItem,
  executeAppAction,
  fetchWorkflowExecutionStates,
  fetchWorkflowExecutionCheckpoints,
  listAllWorkflowExecutions,
  listAllVariablesInCategory,
  createNewTenantVariable,
  listAllTenantVariables,
  updateTenantVariable,
  createTenant,
  manageKeyVal,
  fetchAllApps,
  fetchAppTools,
  fetchAllInsights,
  fetchInsightReports,
  fetchInsightHtml,
  queryDataset,
  prismDiscover,
  prismMeta,
  prismSql,
  prismQuery,
  listGlossary,
  getDataset,
  triggerDatasetSync,
  getChatHistory,
  uploadFile,
  updateBehaviourActionState,
  fetchSORMetrics,
  fetchCitations,
  fetchCitationPresence,
  fetchCitationAppearances,
  fetchCitedDomains,
  fetchPromptCoverage,
  downloadAeoExcel,
  fetchManagedPrompts,
  fetchPrompts,
  fetchPromptDetail,
  fetchPromptUUIDs,
  fetchTopics,
  createTopic,
  createPromptManaged,
  deletePromptManaged,
  fetchBlogPostRefreshTasks,
  updateBlogPostRefreshTask,
  fetchBlogPostOutlineTasks,
  updateBlogPostOutlineTask,
  createInsight,
  createHtmlInsight,
  renameInsight,
  deleteInsight,
  listWorkflowInstanceSchedules,
  createWorkflowInstanceSchedule,
  updateWorkflowInstanceSchedule,
  fetchAEOSchedule,
  updateAEOSchedule,
  listCards,
  getCardById,
  updateCardStatus,
  listChatModes,
  setChatMode,
  createDataset,
  deleteDataset,
  listSkills,
  listTenantSkills,
  createTenantSkill,
  updateTenantSkill,
  deleteTenantSkill,
  listRoutines,
  getRoutine,
  updateRoutine,
  deleteRoutine,
  triggerRoutine,
  deleteWorkflowExecution,
  updateWorkflowExecution,
  listExperiments,
  listKnowledgeHubs,
  getKnowledgeHub,
  createKnowledgeHub,
  updateKnowledgeHub,
  deleteKnowledgeHub,
  listAssignments,
  getAssignment,
  createAssignment,
  fetchSeoAuditTablePage
};
